import firebase from 'firebase/app';
import 'firebase/firestore';
import _ from 'lodash';
import format from 'date-fns/format';
import subDays from 'date-fns/sub_days';
import { getUserAll, getTeamByCompany } from './core/cacheService';
import { t } from '@/plugin/vue-lang';
import axios from 'axios'
import { getCompanyTimezone, toCompanyDate } from './utils/companyTimezone'
import { apiNodeRequest } from './core/main'

// Date range options for historical focus data.
// Exposed as a getter function so labels resolve against the current
// i18n dictionary at read time (the module loads before i18n on cold start,
// so a static array would freeze the English strings).
const DATE_RANGE_VALUES = [7, 30, 60, 90] as const;

export function getDateRangeOptions(): Array<{ label: string; value: DateRangeValue }> {
  return DATE_RANGE_VALUES.map(v => ({
    label: t(`focus.last_${v}_days`),
    value: v as DateRangeValue,
  }));
}

export type DateRangeValue = 7 | 30 | 60 | 90;

interface FocusItem {
  item: string;
  description: string;
  item_id: string;
  status: string;
  goal_id: string | null;
  value_id?: string | null; // Company core value alignment
  userEmail?: string;
  userInfo?: {
    email: string;
    fullname: string;
    firstname: string;
    lastname: string;
    team: string;
    team_name: string;
    large_pic?: string;
    house_color?: string;
  } | null;
}

// Extended focus item with user info for historical data
interface HistoricalFocusItem extends FocusItem {
  userEmail: string;
  userInfo?: {
    email: string;
    fullname: string;
    firstname: string;
    lastname: string;
    team: string;
    team_name: string;
    large_pic?: string;
    house_color?: string;
  };
  date: string; // The snapshot date
}

interface UserFocusData {
  email: string;
  fullname: string;
  firstname: string;
  lastname: string;
  team: string;
  team_name: string;
  large_pic?: string;
  house_color?: string;
  activeItems: FocusItem[];
}

interface PartnershipData {
  company: string;
  date: string;
  email: string;
  item_id: string;
  partner_email: string;
  partner_item_id: string;
  partner_status: string;
  partner_view_description: string;
  user_view_description: string;
}

// Team alignment stats structure
// alignmentRate can be null when no applicable items exist (all goal_ids are null = N/A)
interface TeamAlignmentStats {
  teamId: string;
  totalFocusItems: number;
  alignedItems: number;
  unalignedItems: number;
  alignmentRate: number | null;
  goalStats: Record<string, {
    goalId: string;
    peopleCount: number;
    openCount: number;
    doneCount: number;
    totalItems: number;
    people: Array<{ email: string; fullname: string; large_pic?: string; house_color?: string }>;
  }>;
}

// Team-scoped focus data cache
interface TeamFocusCache {
  focusItems: FocusItem[];
  userEmails: string[];
  loadedAt: number;
  includesChildren: boolean;
  loaded: boolean;
  stats: TeamAlignmentStats;
}

// Historical focus data cache (company-wide, filtered by date range)
interface HistoricalFocusCache {
  company: string;
  dateRange: DateRangeValue;
  loadedAt: number;
  // Raw items grouped by user email, keyed by item_id for deduplication
  itemsByUser: Record<string, Record<string, HistoricalFocusItem>>;
  // All unique items (deduplicated by item_id, keeping most recent status)
  allItems: HistoricalFocusItem[];
  // Stats computed from historical data
  stats: {
    totalUniqueItems: number;
    completedItems: number;
    openItems: number;
    usersWithFocus: number;
    alignedItems: number;
    unalignedItems: number;
  };
}

// Weekly summary data from focus_summary_weekly_team collection
interface WeeklySummary {
  week: string; // Format: "2025-49" (year-week)
  teamId: string;
  company: string;
  focusCoverage: number; // Percentage of users with focus
  alignedItemsPercentage: number; // Goal alignment percentage
  alignedItems: number;
  unalignedItems: number;
  totalItems: number;
  users: number; // Total users in team
  withFocus: number; // Users with at least one focus item
  createdatetime: string;
}

// Week-over-week change calculation result
interface WeekOverWeekChange {
  current: number;
  previous: number | null;
  change: number | null;
  changePercent: number | null;
  trend: 'up' | 'down' | 'neutral' | 'no_data';
}

// Emergent Goals AI feature interfaces
interface SuggestedAssignment {
  itemText: string;
  item_ids: string[];
  suggestedGoalId: string;
  suggestedGoalTitle: string;
  teamName: string;
  confidence: 'high' | 'medium' | 'low';
  reasoning: string;
}

interface EmergentGoal {
  suggestedTitle: string;
  suggestedDescription: string;
  relatedItems: Array<{
    itemText: string;
    item_ids: string[];
  }>;
  priority: 'high' | 'medium' | 'low';
  reasoning: string;
}

interface EmergentGoalsResult {
  suggestedAssignments: SuggestedAssignment[];
  emergentGoals: EmergentGoal[];
  suggestionId?: string;
  generatedAt: string;
  cachedAt?: string;
  autoApplyDone?: boolean;  // Flag to indicate auto-apply has already been done
  error?: string;
}

/**
 * Get team IDs within a specified depth from the selected team
 * Uses the parent field in allTeams to traverse the team hierarchy
 * @param teamId - The starting team ID
 * @param allTeams - Record of all teams keyed by team ID
 * @param maxDepth - Maximum depth to include (0=selected only, 1=selected+direct children, etc.)
 * @returns Set of team IDs within the specified depth
 */
function getTeamsWithinDepth(teamId: string, allTeams: Record<string, any>, maxDepth: number): Set<string> {
  const result = new Set<string>();
  result.add(teamId); // Level 0: Selected team

  if (maxDepth < 1) {
    return result;
  }

  // BFS to get teams up to maxDepth
  let currentLevel = new Set<string>([teamId]);

  for (let depth = 1; depth <= maxDepth; depth++) {
    const nextLevel = new Set<string>();

    // Find all teams whose parent is in the current level
    for (const tid in allTeams) {
      const team = allTeams[tid];
      if (team && team.parent && currentLevel.has(team.parent)) {
        nextLevel.add(tid);
        result.add(tid);
      }
    }

    if (nextLevel.size === 0) {
      break; // No more children found
    }
    currentLevel = nextLevel;
  }

  return result;
}

function normalizePhoneIdentifier(value: string): string {
  if (!value || typeof value !== 'string') return '';
  let normalized = value.trim().replace(/[\s().-]+/g, '');
  if (!normalized) return '';
  if (normalized.startsWith('00')) {
    normalized = `+${normalized.slice(2)}`;
  }
  if (normalized.startsWith('+')) {
    return `+${normalized.slice(1).replace(/\D+/g, '')}`;
  }
  return normalized.replace(/\D+/g, '');
}

function collectUserIdentifierKeys(userKey: string, user: any): string[] {
  const ids = new Set<string>();
  const add = (value: any) => {
    if (typeof value !== 'string') return;
    const normalized = value.trim();
    if (normalized) {
      ids.add(normalized);
    }
  };

  add(userKey);
  add(user && user.email);

  if (user && typeof user.email === 'string') {
    add(user.email.replace(/\./g, '||'));
  }

  const phoneCandidates = [
    user && user.phone,
    user && user.phoneNumber,
    user && user.mobile,
  ];

  phoneCandidates.forEach((value) => {
    const normalizedPhone = normalizePhoneIdentifier(value);
    if (normalizedPhone) {
      ids.add(normalizedPhone);
    }
  });

  return Array.from(ids);
}

function normalizeFocusDateString(value: any): string {
  if (!value) return '';

  if (typeof value.toDate === 'function') {
    return format(value.toDate(), 'YYYY-MM-DD HH:mm:ss');
  }

  const dateString = String(value).trim();
  if (!dateString) return '';

  if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
    return `${dateString} 00:00:00`;
  }

  return dateString.replace('T', ' ').slice(0, 19);
}

function normalizeFocusChecklist(checklist: any): any[] {
  if (Array.isArray(checklist)) return checklist;
  if (typeof checklist === 'object' && checklist !== null) return Object.values(checklist);
  return [];
}

function buildUserLookupMaps(allUsers: Record<string, any>) {
  const userLookup: Record<string, any> = {};
  const focusDocIds = new Set<string>();

  Object.keys(allUsers).forEach((key) => {
    const user = allUsers[key];
    collectUserIdentifierKeys(key, user).forEach((identifier) => {
      userLookup[identifier] = user;
      focusDocIds.add(identifier);
    });
  });

  return {
    userLookup,
    focusDocIds: Array.from(focusDocIds),
  };
}

function isActiveFocusUser(user: any): boolean {
  const status = String((user && user.status) || '').toLowerCase();
  return !!user && status !== 'inactive' && status !== 'deleted';
}

function buildTeamAlignmentStats(teamId: string, focusItems: FocusItem[]): TeamAlignmentStats {
  const totalFocusItems = focusItems.length;
  const alignedItems = focusItems.filter((item: FocusItem) => item.goal_id && item.goal_id !== 'unmatched').length;
  const unalignedItems = focusItems.filter((item: FocusItem) => item.goal_id === 'unmatched').length;
  const applicableItems = alignedItems + unalignedItems;
  const alignmentRate = applicableItems > 0 ? (alignedItems / applicableItems) * 100 : null;

  const goalStats: Record<string, any> = {};
  const usersByGoal: Record<string, Record<string, boolean>> = {};

  focusItems.forEach((item: FocusItem) => {
    if (!item.goal_id || item.goal_id === 'unmatched') {
      return;
    }

    const goalId = item.goal_id;

    if (!goalStats[goalId]) {
      goalStats[goalId] = {
        goalId,
        peopleCount: 0,
        openCount: 0,
        doneCount: 0,
        totalItems: 0,
        people: [],
      };
      usersByGoal[goalId] = {};
    }

    goalStats[goalId].totalItems++;
    if (item.status === 'done') {
      goalStats[goalId].doneCount++;
    } else {
      goalStats[goalId].openCount++;
    }

    if (item.userEmail && !usersByGoal[goalId][item.userEmail]) {
      usersByGoal[goalId][item.userEmail] = true;
      goalStats[goalId].peopleCount++;
      if (item.userInfo) {
        goalStats[goalId].people.push({
          email: item.userEmail,
          fullname: item.userInfo.fullname || item.userEmail,
          large_pic: item.userInfo.large_pic,
          house_color: item.userInfo.house_color,
        });
      }
    }
  });

  return {
    teamId,
    totalFocusItems,
    alignedItems,
    unalignedItems,
    alignmentRate,
    goalStats,
  };
}

/**
 * Resolve a best-effort timestamp (ms) for emergent-goals suggestion docs.
 * Firestore server timestamps may be temporarily unset right after write, so
 * we fall back to client/generated fields to keep cache ordering stable.
 */
function resolveEmergentSuggestionTimeMs(data: any): number {
  if (!data) return 0;

  if (data.createdAt && typeof data.createdAt.toDate === 'function') {
    return data.createdAt.toDate().getTime();
  }

  if (typeof data.createdAt === 'number' && Number.isFinite(data.createdAt)) {
    return data.createdAt;
  }

  if (typeof data.createdAt === 'string') {
    const parsedCreatedAt = Date.parse(data.createdAt);
    if (!Number.isNaN(parsedCreatedAt)) {
      return parsedCreatedAt;
    }
  }

  if (typeof data.createdAtMs === 'number' && Number.isFinite(data.createdAtMs)) {
    return data.createdAtMs;
  }

  if (typeof data.generatedAt === 'string') {
    const parsedGeneratedAt = Date.parse(data.generatedAt);
    if (!Number.isNaN(parsedGeneratedAt)) {
      return parsedGeneratedAt;
    }
  }

  return 0;
}

export default {
  namespaced: true,
  state: {
    loading: false,
    loadingPartners: false,
    loadingHistorical: false,
    userFocusData: [] as UserFocusData[],
    allUsers: {} as any,
    allTeams: {} as any,
    organization1level: {} as Record<string, Record<string, boolean>>,
    organization1levelAll: {} as Record<string, Record<string, boolean>>,
    selectedTeam: '',
    searchKeyword: '',
    filterNoFocus: false,
    filterPartnered: false,
    filterGoalAlignment: 'all' as 'all' | 'aligned' | 'unaligned',
    partnerCache: {} as Record<string, PartnershipData[]>,
    allPartnerships: [] as PartnershipData[],
    currentPage: 1,
    pageSize: 50,
    // Team-scoped focus data cache
    focusDataByTeam: {} as Record<string, TeamFocusCache>,
    loadingTeamFocus: {} as Record<string, boolean>,
    // Historical focus data
    dateRange: 90 as DateRangeValue, // Default to 90 days
    historicalFocusCache: null as HistoricalFocusCache | null,
    // Team-specific historical focus cache (keyed by teamId_dateRange_includeChildren)
    historicalFocusByTeam: {} as Record<string, HistoricalFocusCache>,
    loadingHistoricalTeam: {} as Record<string, boolean>,
    historicalFocusData: [] as HistoricalFocusItem[],
    historicalFocusDataCache: {},
    // Weekly summary data (keyed by teamId, stores array of weekly summaries)
    weeklySummaryByTeam: {} as Record<string, WeeklySummary[]>,
    loadingWeeklySummary: {} as Record<string, boolean>,
    quarterlyFocusByTeam: {} as Record<string, HistoricalFocusCache>,
    // Emergent Goals AI feature state
    emergentGoalsLoading: false,
    emergentGoalsResult: null as EmergentGoalsResult | null,
  },
  mutations: {
    setLoading(state: any, value: boolean) {
      state.loading = value;
    },
    setLoadingPartners(state: any, value: boolean) {
      state.loadingPartners = value;
    },
    setUserFocusData(state: any, data: UserFocusData[]) {
      state.userFocusData = data;
    },
    setAllUsers(state: any, users: any) {
      state.allUsers = users;
    },
    setAllTeams(state: any, teams: any) {
      state.allTeams = teams;
    },
    setOrganization1level(state: any, data: any) {
      state.organization1level = data;
    },
    setOrganization1levelAll(state: any, data: any) {
      state.organization1levelAll = data;
    },
    setSelectedTeam(state: any, team: string) {
      state.selectedTeam = team;
      state.currentPage = 1;
    },
    setAllPartnerships(state: any, data: PartnershipData[]) {
      state.allPartnerships = data;
    },
    setSearchKeyword(state: any, keyword: string) {
      state.searchKeyword = keyword;
      state.currentPage = 1;
    },
    setFilterNoFocus(state: any, value: boolean) {
      state.filterNoFocus = value;
      state.currentPage = 1;
    },
    setFilterPartnered(state: any, value: boolean) {
      state.filterPartnered = value;
      state.currentPage = 1;
    },
    setFilterGoalAlignment(state: any, value: 'all' | 'aligned' | 'unaligned') {
      state.filterGoalAlignment = value;
      state.currentPage = 1;
    },
    setPartnerCache(state: any, { itemId, data }: { itemId: string; data: PartnershipData[] }) {
      state.partnerCache[itemId] = data;
    },
    setCurrentPage(state: any, page: number) {
      state.currentPage = page;
    },
    // Team-scoped focus mutations
    setTeamFocusCache(state: any, { teamId, data }: { teamId: string; data: TeamFocusCache }) {
      state.focusDataByTeam = {
        ...state.focusDataByTeam,
        [teamId]: data,
      };
    },
    setLoadingTeamFocus(state: any, { teamId, loading }: { teamId: string; loading: boolean }) {
      state.loadingTeamFocus = {
        ...state.loadingTeamFocus,
        [teamId]: loading,
      };
    },
    clearTeamFocusCache(state: any) {
      state.focusDataByTeam = {};
      state.loadingTeamFocus = {};
    },
    // Historical focus mutations
    setDateRange(state: any, value: DateRangeValue) {
      state.dateRange = value;
      // Clear historical cache when date range changes
      state.historicalFocusCache = null;
      state.historicalFocusByTeam = {};
    },
    setLoadingHistorical(state: any, value: boolean) {
      state.loadingHistorical = value;
    },
    setHistoricalFocusCache(state: any, data: HistoricalFocusCache | null) {
      state.historicalFocusCache = data;
    },
    setHistoricalFocusByTeam(state: any, { cacheKey, data }: { cacheKey: string; data: HistoricalFocusCache }) {
      state.historicalFocusByTeam = {
        ...state.historicalFocusByTeam,
        [cacheKey]: data,
      };
    },
    setLoadingHistoricalTeam(state: any, { cacheKey, loading }: { cacheKey: string; loading: boolean }) {
      state.loadingHistoricalTeam = {
        ...state.loadingHistoricalTeam,
        [cacheKey]: loading,
      };
    },
    clearHistoricalCache(state: any) {
      state.historicalFocusCache = null;
      state.historicalFocusByTeam = {};
      state.loadingHistoricalTeam = {};
    },
    setHistoricalFocusDataCache(state: any, data: any) {
      state.historicalFocusDataCache = data;
    },
    // Weekly summary mutations
    setWeeklySummaryByTeam(state: any, { teamId, data }: { teamId: string; data: WeeklySummary[] }) {
      state.weeklySummaryByTeam = {
        ...state.weeklySummaryByTeam,
        [teamId]: data,
      };
    },
    setLoadingWeeklySummary(state: any, { teamId, loading }: { teamId: string; loading: boolean }) {
      state.loadingWeeklySummary = {
        ...state.loadingWeeklySummary,
        [teamId]: loading,
      };
    },
    clearWeeklySummaryCache(state: any) {
      state.weeklySummaryByTeam = {};
      state.loadingWeeklySummary = {};
    },
    setQuarterlyFocusByTeam(state: any, data: any) {
      state.quarterlyFocusByTeam = data;
    },
    // Emergent Goals AI mutations
    setEmergentGoalsLoading(state: any, value: boolean) {
      state.emergentGoalsLoading = value;
    },
    setEmergentGoalsResult(state: any, result: EmergentGoalsResult | null) {
      state.emergentGoalsResult = result;
    },
    clearEmergentGoalsResult(state: any) {
      state.emergentGoalsResult = null;
    },
  },
  actions: {
    async loadUsersAndTeams({ commit, rootState }: any) {
      try {
        const company = rootState.authen.user.company;
        const [users, teams, org1levelSnap] = await Promise.all([
          getUserAll(company),
          getTeamByCompany(company),
          firebase.firestore().collection('organization1level').where('company', '==', company).get(),
        ]);
        // Normalize user avatars to ensure large_pic is always populated when possible,
        // following the same pattern used in other views (e.g. insights and members).
        const normalizedUsers: any = {};
        for (const key in users) {
          if (!Object.prototype.hasOwnProperty.call(users, key)) continue;
          const u = users[key];

          // Ensure large_pic has a usable image source
          if (!u.large_pic) {
            if (u.profilepic) {
              u.large_pic = u.profilepic;
            } else if (u.profilepic_crop) {
              u.large_pic = u.profilepic_crop;
            } else if (u.profilepic_small) {
              u.large_pic = u.profilepic_small;
            } else {
              u.large_pic = '';
            }
          }

          normalizedUsers[key] = u;
        }

        commit('setAllUsers', normalizedUsers);
        commit('setAllTeams', teams);
        const orgAll: Record<string, Record<string, boolean>> = {};
        org1levelSnap.forEach((doc) => {
          const data: any = doc.data();
          orgAll[data.team_id] = data.parent || {};
        });
        commit('setOrganization1levelAll', orgAll);
        return { users, teams };
      } catch (error) {
        console.error('Error loading users and teams:', error);
        throw error;
      }
    },

    async loadOrganization1level({ commit, rootState }: any, teamId: string) {
      try {
        const company = rootState.authen.user.company;
        const organization1level: Record<string, Record<string, boolean>> = {};
        
        if (!teamId || teamId === 'All Teams') {
          commit('setOrganization1level', {});
          return {};
        }

        const snapshot = await firebase
          .firestore()
          .collection('organization1level')
          .where('company', '==', company)
          .where('team_id', '==', teamId)
          .get();

        if (snapshot.empty) {
          // If no organization1level doc, just use the team itself
          organization1level[teamId] = { [teamId]: true };
        } else {
          snapshot.forEach((doc) => {
            const data = doc.data();
            organization1level[data.team_id] = data.parent || {};
          });
        }

        commit('setOrganization1level', organization1level);
        return organization1level;
      } catch (error) {
        console.error('Error loading organization1level:', error);
        return {};
      }
    },

    async loadFocusData({ commit, state, rootState, dispatch }: any, teamFilter?: string) {
      commit('setLoading', true);
      try {
        const company = rootState.authen.user.company;
        
        // Ensure users and teams are loaded
        if (Object.keys(state.allUsers).length === 0) {
          await dispatch('loadUsersAndTeams');
        }

        // Load organization1level for hierarchical filtering
        if (teamFilter && teamFilter !== '' && !teamFilter.includes('All Teams')) {
          await dispatch('loadOrganization1level', teamFilter);
        }

        const allUsers = state.allUsers;
        const allTeams = state.allTeams;
        const organization1level = state.organization1level;

        // Filter users by team hierarchy if specified
        let usersInScope = Object.values(allUsers).filter(isActiveFocusUser);
        if (teamFilter && teamFilter !== '' && !teamFilter.includes('All Teams')) {
          // Get all child teams from organization1level
          const childTeams = organization1level[teamFilter] || { [teamFilter]: true };
          const teamIds = Object.keys(childTeams);
          
          usersInScope = usersInScope.filter((user: any) => teamIds.includes(user.team));
        }

        const emails = usersInScope.map((user: any) => user.email).filter(Boolean);
        
        // If no emails, return early with empty data
        if (emails.length === 0) {
          commit('setUserFocusData', []);
          return;
        }
        
        // Chunk emails into groups of 10 for Firestore 'in' query limit
        const chunks = _.chunk(emails, 10);
        const userFocusMap: Record<string, UserFocusData> = {};

        // Initialize all users in scope
        usersInScope.forEach((user: any) => {
          userFocusMap[user.email] = {
            email: user.email,
            fullname: `${user.firstname || ''} ${user.lastname || ''}`.trim(),
            firstname: user.firstname || '',
            lastname: user.lastname || '',
            team: user.team || '',
            team_name: (allTeams[user.team] && allTeams[user.team].name) || '',
            large_pic: user.large_pic,
            house_color: user.house_color,
            activeItems: [],
          };
        });

        // // Fetch focus data in chunks (in parallel) and project only required fields
        // const focusPromises: Array<Promise<firebase.firestore.QuerySnapshot>> = [];
        // for (const chunk of chunks) {
        //   const q = firebase
        //     .firestore()
        //     .collection('focus_summary_latest')
        //     .where(firebase.firestore.FieldPath.documentId(), 'in', chunk);
        //   // @ts-ignore - select is supported in the SDK but not in older typings
        //   const projected = q.select ? q.select('checklist') : q;
        //   focusPromises.push(projected.get());
        // }

        
        
        const focusSnapshots = await firebase.firestore().collection('focus_summary_latest').where('company', '==', company).get();
        focusSnapshots.docs.forEach((doc) => {
          const data = doc.data();
          const email = doc.id;
          
          if (userFocusMap[email] && (data as any).checklist) {
            // Handle both array and object formats for checklist
            let checklistArray: any[] = [];
            const checklist = (data as any).checklist;
            if (Array.isArray(checklist)) {
              checklistArray = checklist;
            } else if (typeof checklist === 'object' && checklist !== null) {
              checklistArray = Object.values(checklist);
            }
                
                // Include all items (both done and open)
                const activeItems = checklistArray
                  .filter((item: any) => item && item.item)
                  .map((item: any) => ({
                    item: item.item || '',
                    description: item.description || '',
                    item_id: item.item_id || '',
                    status: item.status || 'open',
                    goal_id: item.goal_id || null,
                  }));
                
                userFocusMap[email].activeItems = activeItems;
          }
        });

        const userFocusData = Object.values(userFocusMap);
        commit('setUserFocusData', userFocusData);

        // Load ALL partnerships for calculating team metrics accurately
        // Collect all item_ids from all users (not just current page)
        const allItemIds: string[] = [];
        userFocusData.forEach((user: UserFocusData) => {
          user.activeItems.forEach((item: FocusItem) => {
            if (item.item_id) {
              allItemIds.push(item.item_id);
            }
          });
        });

        await dispatch('loadAllPartnerships', { itemIds: allItemIds });
      } catch (error) {
        console.error('Error loading focus data:', error);
      } finally {
        commit('setLoading', false);
      }
    },

    /**
     * Load focus data for a specific team (lazy loading for Goals view)
     * @param teamId - The team ID to load focus data for
     * @param includeChildren - Whether to include child teams in the data
     */
    async loadFocusDataForTeam(
      { commit, state, rootState, dispatch }: any,
      { teamId, includeChildren = false }: { teamId: string; includeChildren?: boolean }
    ) {
      if (!teamId) return null;

      // Create cache key that includes the includeChildren flag
      const cacheKey = `${teamId}_${includeChildren ? 'with_children' : 'direct'}`;

      // Check cache first (valid for 5 minutes)
      const cached = state.focusDataByTeam[cacheKey];
      if (cached && Date.now() - cached.loadedAt < 5 * 60 * 1000) {
        return cached;
      }

      // Check if already loading
      if (state.loadingTeamFocus[cacheKey]) {
        return null;
      }

      commit('setLoadingTeamFocus', { teamId: cacheKey, loading: true });

      try {
        const company = rootState.authen.user.company;

        // Ensure users and teams are loaded
        if (Object.keys(state.allUsers).length === 0) {
          await dispatch('loadUsersAndTeams');
        }

        const allUsers = state.allUsers;
        const allTeams = state.allTeams;

        let teamIds: string[] = [];
        if (teamId && teamId !== 'All Teams') {
          teamIds = [teamId];

          if (includeChildren) {
            const cachedOrg = state.organization1levelAll[teamId];
            if (cachedOrg) {
              teamIds = Object.keys(cachedOrg);
            }

            if (!teamIds.includes(teamId)) {
              teamIds.push(teamId);
            }
          }
        }

        const scopedUsers: Record<string, any> = {};
        Object.keys(allUsers).forEach((key) => {
          const user = allUsers[key];
          if (!teamIds.length || teamIds.includes(user.team)) {
            scopedUsers[key] = user;
          }
        });

        const userInfoLookup: Record<string, any> = {};
        Object.keys(scopedUsers).forEach((key) => {
          const user = scopedUsers[key];
          const info = {
            email: user.email,
            fullname: `${user.firstname || ''} ${user.lastname || ''}`.trim(),
            firstname: user.firstname || '',
            lastname: user.lastname || '',
            team: user.team || '',
            team_name: (allTeams[user.team] && allTeams[user.team].name) || '',
            large_pic: user.large_pic,
            house_color: user.house_color,
          };

          collectUserIdentifierKeys(key, user).forEach((identifier) => {
            userInfoLookup[identifier] = info;
          });
        });

        const { focusDocIds } = buildUserLookupMaps(scopedUsers);

        if (focusDocIds.length === 0) {
          const emptyStats = buildTeamAlignmentStats(teamId, []);
          const emptyCache: TeamFocusCache = {
            focusItems: [],
            userEmails: [],
            loadedAt: Date.now(),
            includesChildren: includeChildren,
            loaded: true,
            stats: emptyStats,
          };
          commit('setTeamFocusCache', { teamId: cacheKey, data: emptyCache });
          return emptyCache;
        }

        const allFocusItems: FocusItem[] = [];
        const userEmailsWithFocus = new Set<string>();
        const processedDocIds = new Set<string>();
        let focusDocs: any[] = [];

        try {
          const chunks = _.chunk(focusDocIds, 10);
          const focusPromises: Array<Promise<firebase.firestore.QuerySnapshot>> = [];

          for (const chunk of chunks) {
            const q = firebase
              .firestore()
              .collection('focus_summary_latest')
              .where(firebase.firestore.FieldPath.documentId(), 'in', chunk);
            // @ts-ignore - select is supported in the SDK but not in older typings
            const projected = q.select ? q.select('checklist') : q;
            focusPromises.push(projected.get());
          }

          const focusSnapshots = await Promise.all(focusPromises);
          focusSnapshots.forEach((snapshot) => {
            focusDocs.push(...snapshot.docs);
          });
        } catch (focusQueryError) {
          console.warn('Falling back to company-wide latest focus query for team scope load:', focusQueryError);
          const q = firebase
            .firestore()
            .collection('focus_summary_latest')
            .where('company', '==', company);
          // @ts-ignore - select is supported in the SDK but not in older typings
          const projected = q.select ? q.select('checklist', 'company') : q;
          const focusSnapshot = await projected.get();
          focusDocs = focusSnapshot.docs;
        }

        focusDocs.forEach((doc) => {
          const docId = doc.id;
          if (processedDocIds.has(docId)) {
            return;
          }

          const normalizedPhoneId = normalizePhoneIdentifier(docId);
          const normalizedEmailId = docId.replace(/\./g, '||');
          const denormalizedEmailId = docId.replace(/\|\|/g, '.');
          const userInfo =
            userInfoLookup[docId] ||
            userInfoLookup[normalizedPhoneId] ||
            userInfoLookup[normalizedEmailId] ||
            userInfoLookup[denormalizedEmailId];

          if (!userInfo) {
            return;
          }

          processedDocIds.add(docId);
          const data = doc.data();
          if (!data.checklist) {
            return;
          }

          let checklistArray: any[] = [];
          const checklist = data.checklist;
          if (Array.isArray(checklist)) {
            checklistArray = checklist;
          } else if (typeof checklist === 'object' && checklist !== null) {
            checklistArray = Object.values(checklist);
          }

          const items = checklistArray
            .filter((item: any) => item && item.item)
            .map((item: any) => ({
              item: item.item || '',
              description: item.description || '',
              item_id: item.item_id || '',
              status: item.status || 'open',
              goal_id: item.goal_id || null,
              userEmail: userInfo.email || docId,
              userInfo,
            }));

          if (items.length > 0) {
            userEmailsWithFocus.add(userInfo.email || docId);
            allFocusItems.push(...items);
          }
        });

        const stats = buildTeamAlignmentStats(teamId, allFocusItems);

        const cacheData: TeamFocusCache = {
          focusItems: allFocusItems,
          userEmails: Array.from(userEmailsWithFocus),
          loadedAt: Date.now(),
          includesChildren: includeChildren,
          loaded: true,
          stats,
        };

        commit('setTeamFocusCache', { teamId: cacheKey, data: cacheData });
        return cacheData;
      } catch (error) {
        console.error('Error loading focus data for team:', teamId, error);
        return null;
      } finally {
        commit('setLoadingTeamFocus', { teamId: cacheKey, loading: false });
      }
    },

    /**
     * Load historical focus data for a specific team from the focus_summary collection
     * Uses company query with client-side date filtering (avoids composite index)
     * @param teamId - The team ID to load focus data for (or 'All Teams')
     * @param includeChildren - Whether to include child teams in the data
     */
    async loadHistoricalFocusDataForTeam(
      { commit, state, rootState, dispatch }: any,
      { teamId, includeChildren = true, startDate: providedStartDate, endDate: providedEndDate, forceRefresh = false, maxDepth }: {
        teamId: string;
        includeChildren?: boolean;
        startDate?: Date;
        endDate?: Date;
        forceRefresh?: boolean;
        maxDepth?: number; // Optional: limit team depth (0=selected only, 1=selected+direct children, undefined=all descendants)
      }
    ) {

      const dateRange = state.dateRange as DateRangeValue;
      // Use provided dates for cache key if available, otherwise use dateRange
      const dateKey = providedStartDate && providedEndDate
        ? `${format(providedStartDate, 'YYYY-MM-DD')}_${format(providedEndDate, 'YYYY-MM-DD')}`
        : String(dateRange);
      const depthKey = maxDepth !== undefined ? `_depth${maxDepth}` : '';
      const cacheKey = `${teamId || 'all'}_${dateKey}_${includeChildren ? 'with_children' : 'direct'}${depthKey}`;
      // Check cache first (valid for 5 minutes) unless forceRefresh is true
      const cached = state.historicalFocusByTeam[cacheKey];
      if (!forceRefresh && cached && Date.now() - cached.loadedAt < 5 * 60 * 1000) {
        return cached;
      }

      // Check if already loading
      if (state.loadingHistoricalTeam[cacheKey]) {
        return null;
      }

      commit('setLoadingHistoricalTeam', { cacheKey, loading: true });

      try {
        const company = rootState.authen.user.company;
        const timezone = await getCompanyTimezone(company)
        const companyNow = toCompanyDate(new Date(), timezone)

        // Ensure users and teams are loaded
        if (Object.keys(state.allUsers).length === 0) {
          await dispatch('loadUsersAndTeams');
        }

        const allUsers = state.allUsers;
        const allTeams = state.allTeams;

        // Calculate start date for filtering - use provided dates if available, otherwise fall back to dateRange from today
        const startDate = providedStartDate
          ? format(providedStartDate, 'YYYY-MM-DD 00:00:00')
          : format(subDays(companyNow, dateRange), 'YYYY-MM-DD 00:00:00');
        const endDate = providedEndDate
          ? format(providedEndDate, 'YYYY-MM-DD 23:59:59')
          : format(companyNow, 'YYYY-MM-DD 23:59:59');
        // Build a reverse lookup from user email/phone to user object
        // This handles different key formats (email with dots, normalized with ||, phone numbers)
        const userLookup: Record<string, any> = {};
        Object.keys(allUsers).forEach((key) => {
          const user = allUsers[key];
          // Index by the original key
          userLookup[key] = user;
          // Also index by email if different from key
          if (user.email && user.email !== key) {
            userLookup[user.email] = user;
          }
          // Index by denormalized key (replace || with .)
          const denormalizedKey = key.replace(/\|\|/g, '.');
          if (denormalizedKey !== key) {
            userLookup[denormalizedKey] = user;
          }
        });

        // Helper function to find user by email/phone with multiple key formats
        const findUser = (emailOrPhone: string) => {
          return userLookup[emailOrPhone] || 
                 userLookup[emailOrPhone.replace(/\./g, '||')] ||
                 allUsers[emailOrPhone] ||
                 allUsers[emailOrPhone.replace(/\./g, '||')];
        };

        // Get team IDs to filter by (if not 'All Teams')
        let teamIds: string[] = [];
        if (teamId && teamId !== 'All Teams' && teamId !== '') {
          teamIds = [teamId];

          if (includeChildren) {
            // If maxDepth is specified, use allTeams to get teams within depth limit
            // Otherwise, use organization1level for all descendants (existing behavior)
            if (maxDepth !== undefined) {
              // Use depth-limited approach via allTeams parent lookup
              const teamsWithinDepth = getTeamsWithinDepth(teamId, allTeams, maxDepth);
              teamIds = Array.from(teamsWithinDepth);
            } else {
              // Use existing organization1level approach for all descendants
              try {
                // Load organization1level for this team to get children
                const org1levelSnap = await firebase
                  .firestore()
                  .collection('organization1level')
                  .where('company', '==', company)
                  .where('team_id', '==', teamId)
                  .get();

                if (!org1levelSnap.empty) {
                  org1levelSnap.forEach((doc) => {
                    const data = doc.data();
                    if (data.parent) {
                      teamIds = Object.keys(data.parent);
                    }
                  });
                }
              } catch (orgError) {
                console.warn('Error loading organization1level, using direct team only:', orgError);
              }

              // Always include the parent team itself
              if (!teamIds.includes(teamId)) {
                teamIds.push(teamId);
              }
            }
          }
        }

        // Filter by date range and optionally by team in JavaScript
        const itemsByUser: Record<string, Record<string, HistoricalFocusItem>> = {};
        const userEmailsSet = new Set<string>();
        const HistoricalFocusData: any[] = [];
        // Query focus_summary by company with limit for performance
        // We limit to 3000 docs to prevent timeouts, then filter by date client-side
        // Note: No orderBy to avoid composite index requirement [[memory:268656]]
        if(state.historicalFocusDataCache.length > 0){
          for(const data of state.historicalFocusDataCache){

            const docDate = data.date;
            const userEmailOrPhone = data.email; // This is actually email OR phone number
            
      
            if(docDate>=startDate ){
              
                 // Find user using the lookup (handles different key formats)
            const user = findUser(userEmailOrPhone);

            // Filter by team if specified
            if (teamIds.length > 0) {
              if (!user || !teamIds.includes(user.team)) {
                continue;
              }
            }

            // Process checklist items
            if (data.checklist) {
              let checklistArray: any[] = [];
              const checklist = data.checklist;
              if (Array.isArray(checklist)) {
                checklistArray = checklist;
              } else if (typeof checklist === 'object' && checklist !== null) {
                checklistArray = Object.values(checklist);
              }
  
              const userInfo = user ? {
                email: userEmailOrPhone,
                fullname: `${user.firstname || ''} ${user.lastname || ''}`.trim(),
                firstname: user.firstname || '',
                lastname: user.lastname || '',
                team: user.team || '',
                team_name: (allTeams[user.team] && allTeams[user.team].name) || '',
                large_pic: user.large_pic,
                house_color: user.house_color,
              } : undefined;
  
              if (!itemsByUser[userEmailOrPhone]) {
                itemsByUser[userEmailOrPhone] = {};
              }
  
              checklistArray
                .filter((item: any) => item && item.item && item.item_id)
                .forEach((item: any) => {
                  const itemId = item.item_id;
                  const existingItem = itemsByUser[userEmailOrPhone][itemId];
  
                  // Keep the most recent version of each item (by date)
                  if (!existingItem || docDate > existingItem.date) {
                    itemsByUser[userEmailOrPhone][itemId] = {
                      item: item.item || '',
                      description: item.description || '',
                      item_id: itemId,
                      status: item.status || 'open',
                      goal_id: item.goal_id || null,
                      userEmail: userEmailOrPhone,
                      userInfo,
                      date: docDate,
                    };
                    userEmailsSet.add(userEmailOrPhone);
                  }
                });
             }

            }
  
         




            

          }

        }else{
            const userInfoCache: Record<string, any> = {};
            const fetchBatchSize = 1000;
            let lastDoc: any = null;
            let totalLoaded = 0;
            let hasMore = true;
        

            
            try {
              const response = await apiNodeRequest({
                method: 'post',
                url: `/focus-summary/${encodeURIComponent(company)}`,
                data: {},
                params: {
                  days: 90,
                },
              })

              if (response.data) {
                response.data.forEach((doc) => {
                const data = doc
                HistoricalFocusData.push(data);
                const docDate = data.date;
                const userEmailOrPhone = data.email; // This is actually email OR phone number
                
                //console.log('data',data)
                // Filter by date range
                if (docDate < startDate) {
                  return;
                }
      
                // Find user using the lookup (handles different key formats)
                const user = findUser(userEmailOrPhone);
      
                // Filter by team if specified
                if (teamIds.length > 0) {
                  if (!user || !teamIds.includes(user.team)) {
                    return;
                  }
                }
      
                // Process checklist items
                if (data.checklist) {
                  let checklistArray: any[] = [];
                  const checklist = data.checklist;
                  if (Array.isArray(checklist)) {
                    checklistArray = checklist;
                  } else if (typeof checklist === 'object' && checklist !== null) {
                    checklistArray = Object.values(checklist);
                  }
      
                  const userInfo = user ? {
                    email: userEmailOrPhone,
                    fullname: `${user.firstname || ''} ${user.lastname || ''}`.trim(),
                    firstname: user.firstname || '',
                    lastname: user.lastname || '',
                    team: user.team || '',
                    team_name: (allTeams[user.team] && allTeams[user.team].name) || '',
                    large_pic: user.large_pic,
                    house_color: user.house_color,
                  } : undefined;
      
                  if (!itemsByUser[userEmailOrPhone]) {
                    itemsByUser[userEmailOrPhone] = {};
                  }
      
                  checklistArray
                    .filter((item: any) => item && item.item && item.item_id)
                    .forEach((item: any) => {
                      const itemId = item.item_id;
                      const existingItem = itemsByUser[userEmailOrPhone][itemId];
      
                      // Keep the most recent version of each item (by date)
                      if (!existingItem || docDate > existingItem.date) {
                        itemsByUser[userEmailOrPhone][itemId] = {
                          item: item.item || '',
                          description: item.description || '',
                          item_id: itemId,
                          status: item.status || 'open',
                          goal_id: item.goal_id || null,
                          userEmail: userEmailOrPhone,
                          userInfo,
                          date: docDate,
                        };
                        userEmailsSet.add(userEmailOrPhone);
                      }
                    });
                }
          });
          commit('setHistoricalFocusDataCache', HistoricalFocusData);







              }
            } catch (error) {
              console.error('Error in historical focus data processing:', error);
            }


            







            // do {
            //   let query = firebase
            //     .firestore()
            //     .collection('focus_summary')
            //     .where('company', '==', company)
            //     .where('date', '>=', startDate)
            //     .where('date', '<=', endDate)
            //     .limit(fetchBatchSize);
              
            //   if (lastDoc) {
            //     query = query.startAfter(lastDoc);
            //   }
              
            //   const snapshot = await query.get();
              
            //   if (snapshot.empty) {
            //     hasMore = false;
            //     break;
            //   }
              
            //   const docsCount = snapshot.docs.length;
            //   totalLoaded += docsCount;
            //   console.log(`Loading historical data: ${totalLoaded} documents...`);
              
            //   // ประมวลผล batch นี้ทันที
            //   for (const doc of snapshot.docs) {
            //     const data = doc.data();
            //     HistoricalFocusData.push(data);
            //     const docDate = data.date;
            //     const userEmailOrPhone = data.email;
                
            //     const user = findUser(userEmailOrPhone);
                
            //     if (teamIds.length > 0) {
            //       if (!user || !teamIds.includes(user.team)) continue;
            //     }
                
            //     if (!userInfoCache[userEmailOrPhone] && user) {
            //       userInfoCache[userEmailOrPhone] = {
            //         email: userEmailOrPhone,
            //         fullname: `${user.firstname || ''} ${user.lastname || ''}`.trim(),
            //         firstname: user.firstname || '',
            //         lastname: user.lastname || '',
            //         team: user.team || '',
            //         team_name: (allTeams[user.team] && allTeams[user.team].name) || '',
            //         large_pic: user.large_pic,
            //         house_color: user.house_color,
            //       };
            //     }
                
            //     if (data.checklist) {
            //       const checklist = data.checklist;
            //       const checklistArray = Array.isArray(checklist) 
            //         ? checklist 
            //         : (typeof checklist === 'object' && checklist !== null ? Object.values(checklist) : []);
                  
            //       const userInfo = userInfoCache[userEmailOrPhone];
                  
            //       if (!itemsByUser[userEmailOrPhone]) {
            //         itemsByUser[userEmailOrPhone] = {};
            //       }
                  
            //       for (let j = 0; j < checklistArray.length; j++) {
            //         const item = checklistArray[j];
            //         if (!item || !item.item || !item.item_id) continue;
                    
            //         const itemId = item.item_id;
            //         const existingItem = itemsByUser[userEmailOrPhone][itemId];
                    
            //         if (!existingItem || docDate > existingItem.date) {
            //           itemsByUser[userEmailOrPhone][itemId] = {
            //             item: item.item || '',
            //             description: item.description || '',
            //             item_id: itemId,
            //             status: item.status || 'open',
            //             goal_id: item.goal_id || null,
            //             userEmail: userEmailOrPhone,
            //             userInfo,
            //             date: docDate,
            //           };
            //           userEmailsSet.add(userEmailOrPhone);
            //         }
            //       }
            //     }
            //   }
              
            //   // ตรวจสอบว่ายังมีข้อมูลอีกไหม
            //   hasMore = docsCount === fetchBatchSize;
            //   if (hasMore) {
            //     lastDoc = snapshot.docs[snapshot.docs.length - 1];
            //   }
              
            // } while (hasMore && lastDoc);

           //commit('setHistoricalFocusDataCache', HistoricalFocusData);
          // snapshot.docs.forEach((doc) => {
          //   const data = doc.data();
          //   HistoricalFocusData.push(data);
          //   const docDate = data.date;
          //   const userEmailOrPhone = data.email; // This is actually email OR phone number
            
          //   //console.log('data',data)
          //   // Filter by date range
          //   if (docDate < startDate) {
          //     return;
          //   }
  
          //   // Find user using the lookup (handles different key formats)
          //   const user = findUser(userEmailOrPhone);
  
          //   // Filter by team if specified
          //   if (teamIds.length > 0) {
          //     if (!user || !teamIds.includes(user.team)) {
          //       return;
          //     }
          //   }
  
          //   // Process checklist items
          //   if (data.checklist) {
          //     let checklistArray: any[] = [];
          //     const checklist = data.checklist;
          //     if (Array.isArray(checklist)) {
          //       checklistArray = checklist;
          //     } else if (typeof checklist === 'object' && checklist !== null) {
          //       checklistArray = Object.values(checklist);
          //     }
  
          //     const userInfo = user ? {
          //       email: userEmailOrPhone,
          //       fullname: `${user.firstname || ''} ${user.lastname || ''}`.trim(),
          //       firstname: user.firstname || '',
          //       lastname: user.lastname || '',
          //       team: user.team || '',
          //       team_name: (allTeams[user.team] && allTeams[user.team].name) || '',
          //       large_pic: user.large_pic,
          //       house_color: user.house_color,
          //     } : undefined;
  
          //     if (!itemsByUser[userEmailOrPhone]) {
          //       itemsByUser[userEmailOrPhone] = {};
          //     }
  
          //     checklistArray
          //       .filter((item: any) => item && item.item && item.item_id)
          //       .forEach((item: any) => {
          //         const itemId = item.item_id;
          //         const existingItem = itemsByUser[userEmailOrPhone][itemId];
  
          //         // Keep the most recent version of each item (by date)
          //         if (!existingItem || docDate > existingItem.date) {
          //           itemsByUser[userEmailOrPhone][itemId] = {
          //             item: item.item || '',
          //             description: item.description || '',
          //             item_id: itemId,
          //             status: item.status || 'open',
          //             goal_id: item.goal_id || null,
          //             userEmail: userEmailOrPhone,
          //             userInfo,
          //             date: docDate,
          //           };
          //           userEmailsSet.add(userEmailOrPhone);
          //         }
          //       });
          //   }
          // });
          // console.log('HistoricalFocusData===',HistoricalFocusData.length)
          // commit('setHistoricalFocusDataCache', HistoricalFocusData);


        }



        

       
   
        // Flatten all items for stats computation
        const allItems: HistoricalFocusItem[] = [];
        Object.values(itemsByUser).forEach((userItems) => {

          allItems.push(...Object.values(userItems));
          //console.log("userItems",userItems)
        });
        // Compute stats
        const completedItems = allItems.filter(item => item.status === 'done').length;
        const openItems = allItems.filter(item => item.status !== 'done').length;
        // Three-state alignment: null = ignored, "unmatched" = unaligned, valid goal_id = aligned
        const alignedItems = allItems.filter(item => item.goal_id && item.goal_id !== 'unmatched').length;
        const unalignedItems = allItems.filter(item => item.goal_id === 'unmatched').length;

        const cacheData: HistoricalFocusCache = {
          company,
          dateRange,
          loadedAt: Date.now(),
          itemsByUser,
          allItems,
          stats: {
            totalUniqueItems: allItems.length,
            completedItems,
            openItems,
            usersWithFocus: userEmailsSet.size,
            alignedItems,
            unalignedItems,
          },
        };
        commit('setHistoricalFocusByTeam', { cacheKey, data: cacheData });
        return cacheData;
      } catch (error) {
        console.error('Error loading historical focus data for team:', teamId, error);
        return null;
      } finally {
        //console.log('setLoadingHistoricalTeam===', cacheKey, false)
        commit('setLoadingHistoricalTeam', { cacheKey, loading: false });
      }
    },

    /**
     * Change the date range for historical focus data
     * Clears the cache and optionally reloads data
     */
    async setDateRange({ commit, dispatch, state }: any, { dateRange, reload = false }: { dateRange: DateRangeValue; reload?: boolean }) {
      commit('setDateRange', dateRange);
      
      if (reload && state.selectedTeam) {
        await dispatch('loadHistoricalFocusDataForTeam', {
          teamId: state.selectedTeam,
          includeChildren: true,
        });
      }
    },

    /**
     * Load weekly summary data for a team from focus_summary_weekly_team collection
     * Fetches last 13 weeks of data for week-over-week comparisons and trend analysis
     * @param teamId - The team ID to load data for (or 'All Teams' for company-wide)
     */
    async loadWeeklySummaryForTeam(
      { commit, state, rootState }: any,
      { teamId }: { teamId: string }
    ) {
      // Use 'all' as key for company-wide data
      const cacheKey = teamId === 'All Teams' || !teamId ? 'all' : teamId;

      // Check if already cached (valid for 5 minutes)
      const cached = state.weeklySummaryByTeam[cacheKey];
      if (cached && cached.length > 0) {
        // Check if first item was loaded within 5 minutes
        const firstItem = cached[0];
        if (firstItem._loadedAt && Date.now() - firstItem._loadedAt < 5 * 60 * 1000) {
          return cached;
        }
      }

      // Check if already loading
      if (state.loadingWeeklySummary[cacheKey]) {
        return null;
      }

      commit('setLoadingWeeklySummary', { teamId: cacheKey, loading: true });

      try {
        const company = rootState.authen.user.company;
        
        // Build query for weekly summary data
        // Note: We avoid orderBy to prevent requiring a composite index
        // Sorting and limiting is done client-side instead
        let query: firebase.firestore.Query = firebase
          .firestore()
          .collection('focus_summary_weekly_team')
          .where('company', '==', company);

        // Filter by team if not 'All Teams'
        if (teamId && teamId !== 'All Teams') {
          query = query.where('team_id', '==', teamId);
        }

        const snapshot = await query.get();
        
        const weeklySummaries: WeeklySummary[] = [];
        const loadedAt = Date.now();

        snapshot.forEach((doc) => {
          const data = doc.data();
          weeklySummaries.push({
            week: data.week || '',
            teamId: data.team_id || '',
            company: data.company || '',
            focusCoverage: data.focus_coverage || 0,
            alignedItemsPercentage: data.alignedItemsPercentage || 0,
            alignedItems: data.alignedItems || 0,
            unalignedItems: data.unalignedItems || 0,
            totalItems: data.totalItems || 0,
            users: data.users || 0,
            withFocus: data.withFocus || 0,
            createdatetime: data.createdatetime || '',
            _loadedAt: loadedAt, // Internal tracking for cache freshness
          } as WeeklySummary & { _loadedAt: number });
        });

        let limitedSummaries: WeeklySummary[];

        // For "All Teams", aggregate data by week (average across all teams)
        if (!teamId || teamId === 'All Teams') {
          const weeklyAggregated: Record<string, { 
            focusCoverage: number[], 
            alignedItemsPercentage: number[],
            alignedItems: number,
            unalignedItems: number,
            totalItems: number,
            users: number,
            withFocus: number,
            count: number 
          }> = {};

          weeklySummaries.forEach((summary) => {
            if (!weeklyAggregated[summary.week]) {
              weeklyAggregated[summary.week] = {
                focusCoverage: [],
                alignedItemsPercentage: [],
                alignedItems: 0,
                unalignedItems: 0,
                totalItems: 0,
                users: 0,
                withFocus: 0,
                count: 0,
              };
            }
            const agg = weeklyAggregated[summary.week];
            agg.focusCoverage.push(summary.focusCoverage);
            agg.alignedItemsPercentage.push(summary.alignedItemsPercentage);
            agg.alignedItems += summary.alignedItems;
            agg.unalignedItems += summary.unalignedItems;
            agg.totalItems += summary.totalItems;
            agg.users += summary.users;
            agg.withFocus += summary.withFocus;
            agg.count++;
          });

          // Convert aggregated data back to WeeklySummary format
          const aggregatedSummaries: WeeklySummary[] = Object.keys(weeklyAggregated).map((week) => {
            const agg = weeklyAggregated[week];
            // Calculate focus coverage as percentage of users with focus
            const focusCoverage = agg.users > 0 ? (agg.withFocus / agg.users) * 100 : 0;
            // Calculate alignment as percentage of aligned items
            const totalApplicableItems = agg.alignedItems + agg.unalignedItems;
            const alignedItemsPercentage = totalApplicableItems > 0 
              ? (agg.alignedItems / totalApplicableItems) * 100 
              : 0;
            
            return {
              week,
              teamId: 'all',
              company: rootState.authen.user.company,
              focusCoverage: Math.round(focusCoverage * 10) / 10,
              alignedItemsPercentage: Math.round(alignedItemsPercentage * 10) / 10,
              alignedItems: agg.alignedItems,
              unalignedItems: agg.unalignedItems,
              totalItems: agg.totalItems,
              users: agg.users,
              withFocus: agg.withFocus,
              createdatetime: '',
              _loadedAt: loadedAt,
            } as WeeklySummary & { _loadedAt: number };
          });

          // Sort by week descending and limit
          aggregatedSummaries.sort((a, b) => b.week.localeCompare(a.week));
          limitedSummaries = aggregatedSummaries.slice(0, 13);
        } else {
          // For specific team, just sort and limit
          weeklySummaries.sort((a, b) => b.week.localeCompare(a.week));
          limitedSummaries = weeklySummaries.slice(0, 13);
        }

        commit('setWeeklySummaryByTeam', { teamId: cacheKey, data: limitedSummaries });
        return limitedSummaries;
      } catch (error) {
        console.error('Error loading weekly summary for team:', teamId, error);
        return null;
      } finally {
        commit('setLoadingWeeklySummary', { teamId: cacheKey, loading: false });
      }
    },

    async loadAllPartnerships({ commit, state, getters }: any, payload?: { itemIds?: string[] }) {
      try {
        // Collect item_ids (optionally only for visible page to reduce reads)
        let allItemIds: string[] = [];
        if (payload && payload.itemIds && payload.itemIds.length > 0) {
          allItemIds = payload.itemIds;
        } else {
          // Default to the currently visible filtered page
          const start = (state.currentPage - 1) * state.pageSize;
          const end = start + state.pageSize;
          const usersPage: UserFocusData[] = (getters.filteredUsers || state.userFocusData).slice(start, end);
          usersPage.forEach((user: UserFocusData) => {
            user.activeItems.forEach((item: FocusItem) => {
              if (item.item_id) {
                allItemIds.push(item.item_id);
              }
            });
          });
        }

        if (allItemIds.length === 0) {
          commit('setAllPartnerships', []);
          return;
        }

        // Chunk item_ids into groups of 10 for Firestore 'in' query limit
        const chunks = _.chunk(allItemIds, 10);
        const allPartnerships: PartnershipData[] = [];

        const partnerPromises: Array<Promise<firebase.firestore.QuerySnapshot>> = [];
        chunks.forEach((chunk) => {
          if (chunk.length > 0) {
            partnerPromises.push(
              firebase.firestore().collection('focus_partnerships')
                .where('item_id', 'in', chunk)
                .get()
            );
            partnerPromises.push(
              firebase.firestore().collection('focus_partnerships')
                .where('partner_item_id', 'in', chunk)
                .get()
            );
          }
        });
        const partnerSnapshots = await Promise.all(partnerPromises);
        partnerSnapshots.forEach((snap, idx) => {
          const isReverse = idx % 2 === 1; // second query in each pair is reverse
          snap.forEach((doc) => {
            const data = doc.data() as PartnershipData;
            if (!isReverse) {
              allPartnerships.push(data);
            } else {
              allPartnerships.push({
                company: data.company,
                date: data.date,
                email: data.partner_email,
                item_id: data.partner_item_id,
                partner_email: data.email,
                partner_item_id: data.item_id,
                partner_status: data.partner_status,
                partner_view_description: data.user_view_description,
                user_view_description: data.partner_view_description,
              });
            }
          });
        });

        commit('setAllPartnerships', allPartnerships);
        
        // Also populate the cache
        const cacheByItemId: Record<string, PartnershipData[]> = {};
        allPartnerships.forEach((partnership) => {
          if (!cacheByItemId[partnership.item_id]) {
            cacheByItemId[partnership.item_id] = [];
          }
          cacheByItemId[partnership.item_id].push(partnership);
        });

        Object.keys(cacheByItemId).forEach((itemId) => {
          const uniquePartnerships = _.uniqBy(
            cacheByItemId[itemId],
            (p) => `${p.partner_email}-${p.partner_item_id}`
          );
          commit('setPartnerCache', { itemId, data: uniquePartnerships });
        });
      } catch (error) {
        console.error('Error loading all partnerships:', error);
      }
    },

    async loadPartnersForItem({ commit, state, rootState }: any, itemId: string) {
      // Check cache first
      if (state.partnerCache[itemId]) {
        return state.partnerCache[itemId];
      }

      commit('setLoadingPartners', true);
      try {
        // Query both directions: where this item is the main item and where it's the partner item
        const [snapshot1, snapshot2] = await Promise.all([
          firebase
            .firestore()
            .collection('focus_partnerships')
            .where('item_id', '==', itemId)
            .get(),
          firebase
            .firestore()
            .collection('focus_partnerships')
            .where('partner_item_id', '==', itemId)
            .get(),
        ]);

        const partnerships: PartnershipData[] = [];
        
        snapshot1.forEach((doc) => {
          partnerships.push(doc.data() as PartnershipData);
        });
        
        snapshot2.forEach((doc) => {
          const data = doc.data() as PartnershipData;
          // Swap perspective for reverse partnerships
          partnerships.push({
            company: data.company,
            date: data.date,
            email: data.partner_email,
            item_id: data.partner_item_id,
            partner_email: data.email,
            partner_item_id: data.item_id,
            partner_status: data.partner_status,
            partner_view_description: data.user_view_description,
            user_view_description: data.partner_view_description,
          });
        });

        // Remove duplicates based on partner_email + partner_item_id
        const uniquePartnerships = _.uniqBy(partnerships, (p) => `${p.partner_email}-${p.partner_item_id}`);
        
        commit('setPartnerCache', { itemId, data: uniquePartnerships });
        return uniquePartnerships;
      } catch (error) {
        console.error('Error loading partners:', error);
        return [];
      } finally {
        commit('setLoadingPartners', false);
      }
    },

    async changeTeam({ commit, dispatch }: any, team: string) {
      commit('setSelectedTeam', team);
      await dispatch('loadFocusData', team);
    },

    searchUsers({ commit, dispatch, getters }: any, keyword: string) {
      commit('setSearchKeyword', keyword);
      // Reload partnerships for the newly visible (filtered) page
      const usersPage: UserFocusData[] = getters.paginatedUsers || [];
      const itemIds: string[] = [];
      usersPage.forEach((user: UserFocusData) => {
        user.activeItems.forEach((item: FocusItem) => {
          if (item.item_id) itemIds.push(item.item_id);
        });
      });
      dispatch('loadAllPartnerships', { itemIds });
    },

    toggleFilterNoFocus({ commit, state, dispatch, getters }: any) {
      commit('setFilterNoFocus', !state.filterNoFocus);
      // Reload partnerships for filtered page
      const usersPage: UserFocusData[] = getters.paginatedUsers || [];
      const itemIds: string[] = [];
      usersPage.forEach((user: UserFocusData) => {
        user.activeItems.forEach((item: FocusItem) => {
          if (item.item_id) itemIds.push(item.item_id);
        });
      });
      dispatch('loadAllPartnerships', { itemIds });
    },

    toggleFilterPartnered({ commit, state, dispatch, getters }: any) {
      commit('setFilterPartnered', !state.filterPartnered);
      // Reload partnerships for filtered page
      const usersPage: UserFocusData[] = getters.paginatedUsers || [];
      const itemIds: string[] = [];
      usersPage.forEach((user: UserFocusData) => {
        user.activeItems.forEach((item: FocusItem) => {
          if (item.item_id) itemIds.push(item.item_id);
        });
      });
      dispatch('loadAllPartnerships', { itemIds });
    },

    setGoalAlignmentFilter({ commit, dispatch, getters }: any, value: 'all' | 'aligned' | 'unaligned') {
      commit('setFilterGoalAlignment', value);
      // Reload partnerships for filtered page
      const usersPage: UserFocusData[] = getters.paginatedUsers || [];
      const itemIds: string[] = [];
      usersPage.forEach((user: UserFocusData) => {
        user.activeItems.forEach((item: FocusItem) => {
          if (item.item_id) itemIds.push(item.item_id);
        });
      });
      dispatch('loadAllPartnerships', { itemIds });
    },

    async setPage({ commit, dispatch, state, getters }: any, page: number) {
      commit('setCurrentPage', page);
      // Preload partnerships for visible page only to speed up initial render
      const start = (state.currentPage - 1) * state.pageSize;
      const end = start + state.pageSize;
      const usersPage = (getters.filteredUsers || state.userFocusData).slice(start, end);
      const itemIds: string[] = [];
      usersPage.forEach((user: UserFocusData) => {
        user.activeItems.forEach((item: FocusItem) => {
          if (item.item_id) itemIds.push(item.item_id);
        });
      });
      await dispatch('loadAllPartnerships', { itemIds });
    },
    async getQuarterlyFocusData({ commit, state, rootState, dispatch }: any, { startDate, endDate, teamId, includeChildren = true }: { startDate: Date, endDate: Date, teamId: string, includeChildren?: boolean }) {
      try {
        if (Object.keys(state.allUsers).length === 0) {
          await dispatch('loadUsersAndTeams');
        }
        const allUsers = state.allUsers;
        const allTeams = state.allTeams;
        const userLookup: Record<string, any> = {};
        Object.keys(allUsers).forEach((key) => {
          const user = allUsers[key];
          // Index by the original key
          userLookup[key] = user;
          // Also index by email if different from key
          if (user.email && user.email !== key) {
            userLookup[user.email] = user;
          }
          // Index by denormalized key (replace || with .)
          const denormalizedKey = key.replace(/\|\|/g, '.');
          if (denormalizedKey !== key) {
            userLookup[denormalizedKey] = user;
          }
        });

        // Helper function to find user by email/phone with multiple key formats
        const findUser = (emailOrPhone: string) => {
          return userLookup[emailOrPhone] || 
                 userLookup[emailOrPhone.replace(/\./g, '||')] ||
                 allUsers[emailOrPhone] ||
                 allUsers[emailOrPhone.replace(/\./g, '||')];
        };

            // Get team IDs to filter by (if not 'All Teams')
            let teamIds: string[] = [];
            if (teamId && teamId !== 'All Teams' && teamId !== '') {
              teamIds = [teamId];

              if (includeChildren) {
                try {
                  // Load organization1level for this team to get children
                  const org1levelSnap = await firebase
                    .firestore()
                    .collection('organization1level')
                    .where('company', '==', rootState.authen.user.company)
                    .where('team_id', '==', teamId)
                    .get();

                  if (!org1levelSnap.empty) {
                    org1levelSnap.forEach((doc) => {
                      const data = doc.data();
                      if (data.parent) {
                        teamIds = Object.keys(data.parent);
                      }
                    });
                  }
                } catch (orgError) {
                  console.warn('Error loading organization1level, using direct team only:', orgError);
                }

                // Always include the parent team itself
                if (!teamIds.includes(teamId)) {
                  teamIds.push(teamId);
                }
              }
            }
            // Filter by date range and optionally by team in JavaScript
            const itemsByUser: Record<string, Record<string, HistoricalFocusItem>> = {};
            const userEmailsSet = new Set<string>();

            const HistoricalFocusData: any[] = [];
            const _startDate = format(startDate, 'YYYY-MM-DD');
            const _endDate = format(endDate, 'YYYY-MM-DD');
            try {
              // Use pagination to prevent timeout - fetch in batches
              const fetchBatchSize = 1000; // Limit documents per batch
              let lastDoc: any = null;
              let hasMore = true;
              let totalLoaded = 0;

              // Fetch data in batches using pagination
              do {
                let query = firebase.firestore()
                  .collection('focus_summary')
                  .where('company', '==', rootState.authen.user.company)
                  .where('date', '>=', _startDate)
                  .where('date', '<=', _endDate)
                  .orderBy('date', 'asc') // Required for pagination
                  .limit(fetchBatchSize);

                if (lastDoc) {
                  query = query.startAfter(lastDoc);
                }

                const response = await query.get();
                
                if (response.empty) {
                  hasMore = false;
                  break;
                }

                const docsCount = response.docs.length;
                totalLoaded += docsCount;

                // Process this batch
                response.docs.forEach(doc => {
                  const data = doc.data();
                  HistoricalFocusData.push(data);
                  const docDate = data.date;
                  const userEmailOrPhone = data.email; // This is actually email OR phone number
                  
                  // Filter by date range
                  if (docDate < _startDate) {
                    return;
                  }
        
                  // Find user using the lookup (handles different key formats)
                  const user = findUser(userEmailOrPhone);
                  if(!user){
                    return;
                  }
                  // Filter by team if specified
                  if (teamIds.length > 0) {
                    if (!user || !teamIds.includes(user.team)) {
                      return;
                    }
                  }
        
                  // Process checklist items
                  if (data.checklist) {
                    let checklistArray: any[] = [];
                    const checklist = data.checklist;
                    if (Array.isArray(checklist)) {
                      checklistArray = checklist;
                    } else if (typeof checklist === 'object' && checklist !== null) {
                      checklistArray = Object.values(checklist);
                    }
                    const userInfo = user ? {
                      email: userEmailOrPhone,
                      fullname: `${user.firstname || ''} ${user.lastname || ''}`.trim(),
                      firstname: user.firstname || '',
                      lastname: user.lastname || '',
                      team: user.team || '',
                      team_name: (allTeams[user.team] && allTeams[user.team].name) || '',
                      large_pic: user.large_pic,
                      house_color: user.house_color,
                    } : undefined;
        
                    if (!itemsByUser[userEmailOrPhone]) {
                      itemsByUser[userEmailOrPhone] = {};
                    }
        
                    checklistArray
                      .filter((item: any) => item && item.item && item.item_id)
                      .forEach((item: any) => {
                        const itemId = item.item_id;
                        const existingItem = itemsByUser[userEmailOrPhone][itemId];
        
                        // Keep the most recent version of each item (by date)
                        if (!existingItem || docDate > existingItem.date) {
                          itemsByUser[userEmailOrPhone][itemId] = {
                            item: item.item || '',
                            description: item.description || '',
                            item_id: itemId,
                            status: item.status || 'open',
                            goal_id: item.goal_id || null,
                            userEmail: userEmailOrPhone,
                            userInfo,
                            date: docDate,
                          };
                          userEmailsSet.add(userEmailOrPhone);
                        }
                      });
                  }
                });

                // Check if there's more data to fetch
                hasMore = docsCount === fetchBatchSize;
                if (hasMore) {
                  lastDoc = response.docs[response.docs.length - 1];
                }

              } while (hasMore && lastDoc);

              // Flatten all items for stats computation
              const allItems: HistoricalFocusItem[] = [];
              Object.values(itemsByUser).forEach((userItems) => {
                allItems.push(...Object.values(userItems));
                //console.log("userItems",userItems)
              });
              
              // Compute stats
              const completedItems = allItems.filter(item => item.status === 'done').length;
              const openItems = allItems.filter(item => item.status !== 'done').length;
              // Three-state alignment: null = ignored, "unmatched" = unaligned, valid goal_id = aligned
              const alignedItems = allItems.filter(item => item.goal_id && item.goal_id !== 'unmatched').length;
              const unalignedItems = allItems.filter(item => item.goal_id === 'unmatched').length;
      
              const cacheData: HistoricalFocusCache = {
                company: rootState.authen.user.company,
                dateRange: 90 as DateRangeValue,
                loadedAt: Date.now(),
                itemsByUser,
                allItems,
                stats: {
                  totalUniqueItems: allItems.length,
                  completedItems,
                  openItems,
                  usersWithFocus: userEmailsSet.size,
                  alignedItems,
                  unalignedItems,
                },
              };
              commit('setQuarterlyFocusByTeam', cacheData);

              return cacheData;
            } catch (error) {
              console.error('Error in quarterly focus data pagination:', error);
              return null;
            }



      } catch (error) {
        console.error('Error getting quarterly focus data:', error);
        return null;
      }
    },

    /**
     * Discover emergent goals by analyzing unmatched focus items
     * Calls the AI endpoint to suggest goal assignments and new goals
     */
    async discoverEmergentGoals(
      { commit, rootState, rootGetters }: any,
      { teamId, unmatchedFocusItems, existingGoals }: {
        teamId: string;
        unmatchedFocusItems: Array<{
          itemText: string;
          description: string;
          item_ids: string[];
          count: number;
          people: Array<{ email: string; fullname: string }>;
        }>;
        existingGoals: Array<{
          goal_id: string;
          quick_preview: string;
          goal_description: string;
          teamId: string;
          teamName: string;
          isAncestor: boolean;
        }>;
      }
    ): Promise<EmergentGoalsResult | null> {
      commit('setEmergentGoalsLoading', true);
      commit('setEmergentGoalsResult', null);

      try {
        const company = rootState.authen.user.company;
        const apiUrl = process.env.VUE_APP_API_URL || 'https://api-node.happily.ai';

        const response = await axios.post(`${apiUrl}/ai/discover_emergent_goals`, {
          company,
          teamId,
          unmatchedFocusItems,
          existingGoals,
        });

        const result: EmergentGoalsResult = response.data;

        // Save to cache immediately and get suggestionId
        // This ensures suggestionId is available when the watcher triggers autoApply
        if (result && !result.error) {
          const docRef = await firebase.firestore().collection('emergent_goals_suggestions').add({
            company,
            teamId,
            createdAt: firebase.firestore.FieldValue.serverTimestamp(),
            createdAtMs: Date.now(),
            status: 'pending',
            suggestedAssignments: result.suggestedAssignments,
            emergentGoals: result.emergentGoals,
            generatedAt: result.generatedAt,
          });
          result.suggestionId = docRef.id;
        }

        commit('setEmergentGoalsResult', result);
        return result;
      } catch (error) {
        console.error('Error discovering emergent goals:', error);
        const errorResult: EmergentGoalsResult = {
          suggestedAssignments: [],
          emergentGoals: [],
          generatedAt: new Date().toISOString(),
          error: error.message || 'Failed to discover emergent goals',
        };
        commit('setEmergentGoalsResult', errorResult);
        return errorResult;
      } finally {
        commit('setEmergentGoalsLoading', false);
      }
    },

    /**
     * Update goal_id for focus items (assign items to goals)
     * Called after accepting AI suggestions
     * Updates focus_summary_latest collection directly via Firebase
     *
     * Note: We match items by BOTH item_id AND normalized item text because:
     * - item_id from historical data may not match current focus_summary_latest
     * - Item text matching ensures we catch items even if IDs differ
     */
    async updateFocusItemGoalIds(
      { rootState, dispatch, commit }: any,
      itemUpdates: Array<{
        item_ids: string[];
        goal_id: string;
        itemText?: string; // Optional: the text of the focus item for text-based matching
      }>
    ): Promise<{ success: boolean; updatedCount: number; error?: string }> {
      try {
        if (!itemUpdates || itemUpdates.length === 0) {
          return { success: true, updatedCount: 0 };
        }

        const company = rootState.authen.user.company;

        // Build maps for both item_id and normalized text matching
        const itemIdToGoalMap = new Map<string, string>();
        const itemTextToGoalMap = new Map<string, string>();

        for (const update of itemUpdates) {
          // Map item IDs to goal
          for (const itemId of update.item_ids) {
            itemIdToGoalMap.set(itemId, update.goal_id);
          }
          // Also map normalized item text to goal (for fallback matching)
          if (update.itemText) {
            const normalizedText = update.itemText.toLowerCase().trim();
            if (normalizedText) {
              itemTextToGoalMap.set(normalizedText, update.goal_id);
            }
          }
        }

        // Query focus_summary_latest for documents with matching company
        const snapshot = await firebase.firestore()
          .collection('focus_summary_latest')
          .where('company', '==', company)
          .get();

        const batch = firebase.firestore().batch();
        let updatedCount = 0;
        let docsUpdated = 0;
        let matchedByText = 0;

        for (const doc of snapshot.docs) {
          const data = doc.data();
          if (!data.checklist || !Array.isArray(data.checklist)) {
            continue;
          }

          let docNeedsUpdate = false;
          const updatedChecklist = data.checklist.map((item: any) => {
            // Skip items that already have a goal assigned (not 'unmatched')
            if (item.goal_id && item.goal_id !== 'unmatched') {
              return item;
            }

            // First try to match by item_id
            if (item.item_id && itemIdToGoalMap.has(item.item_id)) {
              docNeedsUpdate = true;
              updatedCount++;
              return {
                ...item,
                goal_id: itemIdToGoalMap.get(item.item_id),
              };
            }

            // Fallback: match by normalized item text
            const normalizedItemText = (item.item || '').toLowerCase().trim();
            if (normalizedItemText && itemTextToGoalMap.has(normalizedItemText)) {
              docNeedsUpdate = true;
              updatedCount++;
              matchedByText++;
              return {
                ...item,
                goal_id: itemTextToGoalMap.get(normalizedItemText),
              };
            }

            return item;
          });

          if (docNeedsUpdate) {
            batch.update(doc.ref, { checklist: updatedChecklist });
            docsUpdated++;
          }
        }

        if (docsUpdated > 0) {
          await batch.commit();
          commit('clearTeamFocusCache');
          // Clear all historical caches since focus item alignment data has changed
          // This ensures the UI reflects the updated goal assignments
          commit('clearHistoricalCache');
        }

        return { success: true, updatedCount };
      } catch (error) {
        console.error('Error updating focus item goal IDs:', error);
        return {
          success: false,
          updatedCount: 0,
          error: error.message || 'Failed to update focus items',
        };
      }
    },

    /**
     * Save emergent goals suggestions to Firebase for later review
     */
    async saveEmergentGoalsSuggestions(
      { rootState, state, commit }: any,
      { teamId, result }: { teamId: string; result: EmergentGoalsResult }
    ): Promise<string | null> {
      try {
        const company = rootState.authen.user.company;

        const docRef = await firebase.firestore().collection('emergent_goals_suggestions').add({
          company,
          teamId,
          createdAt: firebase.firestore.FieldValue.serverTimestamp(),
          createdAtMs: Date.now(),
          status: 'pending',
          suggestedAssignments: result.suggestedAssignments,
          emergentGoals: result.emergentGoals,
          generatedAt: result.generatedAt,
        });

        // Update the store with the suggestionId so auto-apply can mark it as done
        if (state.emergentGoalsResult) {
          commit('setEmergentGoalsResult', {
            ...state.emergentGoalsResult,
            suggestionId: docRef.id,
          });
        }

        return docRef.id;
      } catch (error) {
        console.error('Error saving emergent goals suggestions:', error);
        return null;
      }
    },

    /**
     * Update the status of an emergent goals suggestion
     */
    async updateEmergentGoalsSuggestionStatus(
      { rootState }: any,
      { suggestionId, status, reviewedBy }: { suggestionId: string; status: 'pending' | 'reviewed'; reviewedBy?: string }
    ): Promise<boolean> {
      try {
        await firebase.firestore().collection('emergent_goals_suggestions').doc(suggestionId).update({
          status,
          reviewedAt: firebase.firestore.FieldValue.serverTimestamp(),
          reviewedBy: reviewedBy || rootState.authen.user.email,
        });
        return true;
      } catch (error) {
        console.error('Error updating suggestion status:', error);
        return false;
      }
    },

    /**
     * Mark auto-apply as done for a cached suggestion
     * Called after high-confidence matches are auto-applied
     */
    async markAutoApplyDone(
      { state, commit }: any,
      { suggestionId }: { suggestionId: string }
    ): Promise<boolean> {
      try {
        if (!suggestionId) {
          return false;
        }

        await firebase.firestore().collection('emergent_goals_suggestions').doc(suggestionId).update({
          autoApplyDone: true,
        });

        // Also update the local state
        if (state.emergentGoalsResult && state.emergentGoalsResult.suggestionId === suggestionId) {
          commit('setEmergentGoalsResult', {
            ...state.emergentGoalsResult,
            autoApplyDone: true,
          });
        }

        return true;
      } catch (error) {
        console.error('Error marking auto-apply done:', error);
        return false;
      }
    },

    /**
     * Load cached emergent goals suggestions from Firebase
     * Returns the most recent pending suggestion for the team if it exists and is fresh (< 24 hours)
     */
    async loadCachedEmergentGoalsSuggestions(
      { rootState, commit }: any,
      {
        teamId,
        maxAgeHours = 24,
        allowedGoalIds,
      }: { teamId: string; maxAgeHours?: number; allowedGoalIds?: string[] }
    ): Promise<EmergentGoalsResult | null> {
      // Set loading state to show loading UI during cache check
      commit('setEmergentGoalsLoading', true);

      try {
        const company = rootState.authen.user.company;
        const cutoffTime = new Date();
        cutoffTime.setHours(cutoffTime.getHours() - maxAgeHours);

        // Query without orderBy to avoid needing a composite index
        // We'll sort in JavaScript instead
        const snapshot = await firebase.firestore()
          .collection('emergent_goals_suggestions')
          .where('company', '==', company)
          .where('teamId', '==', teamId)
          .where('status', '==', 'pending')
          .get();

        if (snapshot.empty) {
          commit('setEmergentGoalsLoading', false);
          return null;
        }

        // Sort by createdAt descending in JavaScript and get the most recent
        const sortedDocs = snapshot.docs.sort((a, b) => {
          const aTime = resolveEmergentSuggestionTimeMs(a.data());
          const bTime = resolveEmergentSuggestionTimeMs(b.data());
          return bTime - aTime; // Descending order
        });

        const doc = sortedDocs[0];
        const data = doc.data();

        // Check if the cached data is fresh enough
        const createdAtMs = resolveEmergentSuggestionTimeMs(data);
        if (createdAtMs <= 0 || createdAtMs < cutoffTime.getTime()) {
          commit('setEmergentGoalsLoading', false);
          return null;
        }
        const createdAt = new Date(createdAtMs);

        // Validate cached suggestions against allowed goal IDs (prevents cache bypassing scoping rules)
        // Note: even an empty allowed set should reject any cached assignments with a suggestedGoalId.
        if (Array.isArray(allowedGoalIds)) {
          const allowed = new Set(allowedGoalIds);
          const assignments = Array.isArray(data.suggestedAssignments) ? data.suggestedAssignments : [];
          const hasOutOfScope = assignments.some((a: any) => {
            const goalId = a && a.suggestedGoalId;
            return goalId && !allowed.has(goalId);
          });

          if (hasOutOfScope) {
            // Best-effort invalidate so we don't keep hitting the same bad cache
            try {
              await doc.ref.update({
                status: 'invalidated',
                invalidatedAt: firebase.firestore.FieldValue.serverTimestamp(),
                invalidationReason: 'out_of_scope_goal',
              });
            } catch (e) {
              // Ignore invalidation errors; treat as cache miss regardless
              console.warn('Failed to invalidate out-of-scope emergent goals cache:', e);
            }
            commit('setEmergentGoalsLoading', false);
            return null;
          }
        }

        const result: EmergentGoalsResult = {
          suggestedAssignments: data.suggestedAssignments || [],
          emergentGoals: data.emergentGoals || [],
          generatedAt: data.generatedAt || createdAt.toISOString(),
          cachedAt: createdAt.toISOString(),
          suggestionId: doc.id,
          autoApplyDone: data.autoApplyDone || false,  // Include auto-apply status from cache
        };

        commit('setEmergentGoalsResult', result);
        commit('setEmergentGoalsLoading', false);
        return result;
      } catch (error) {
        console.error('Error loading cached emergent goals suggestions:', error);
        commit('setEmergentGoalsLoading', false);
        return null;
      }
    },

    /**
     * Clear/invalidate cached emergent goals suggestions for a team
     * Called when focus items or goals change significantly
     */
    async invalidateEmergentGoalsCache(
      { rootState }: any,
      { teamId }: { teamId: string }
    ): Promise<boolean> {
      try {
        const company = rootState.authen.user.company;

        const snapshot = await firebase.firestore()
          .collection('emergent_goals_suggestions')
          .where('company', '==', company)
          .where('teamId', '==', teamId)
          .where('status', '==', 'pending')
          .get();

        const batch = firebase.firestore().batch();
        snapshot.docs.forEach(doc => {
          batch.update(doc.ref, { status: 'invalidated' });
        });

        await batch.commit();
        return true;
      } catch (error) {
        console.error('Error invalidating cache:', error);
        return false;
      }
    },
  },
  getters: {
    loading: (state: any) => state.loading,
    loadingPartners: (state: any) => state.loadingPartners,
    // Emergent Goals getters
    emergentGoalsLoading: (state: any) => state.emergentGoalsLoading,
    emergentGoalsResult: (state: any) => state.emergentGoalsResult,
    
    filteredUsers: (state: any) => {
      let users = [...state.userFocusData];

      // Apply search filter
      if (state.searchKeyword) {
        const keyword = state.searchKeyword.toLowerCase();
        users = users.filter((user: UserFocusData) =>
          user.fullname.toLowerCase().includes(keyword) ||
          user.email.toLowerCase().includes(keyword) ||
          user.team_name.toLowerCase().includes(keyword)
        );
      }

      // Apply no-focus filter
      if (state.filterNoFocus) {
        users = users.filter((user: UserFocusData) => user.activeItems.length === 0);
      }

      // Apply partnered filter
      if (state.filterPartnered) {
        users = users.filter((user: UserFocusData) => user.activeItems.length > 0);
      }

      // Apply goal alignment filter (three-state: null = ignored, "unmatched" = unaligned, valid goal_id = aligned)
      if (state.filterGoalAlignment === 'aligned') {
        // Show users with at least one item that has a valid goal_id (not null, not "unmatched")
        users = users.filter((user: UserFocusData) =>
          user.activeItems.some((item: FocusItem) => item.goal_id && item.goal_id !== 'unmatched')
        );
      } else if (state.filterGoalAlignment === 'unaligned') {
        // Show users with at least one item that is "unmatched" (goals exist but no match)
        users = users.filter((user: UserFocusData) =>
          user.activeItems.some((item: FocusItem) => item.goal_id === 'unmatched')
        );
      }

      return users;
    },

    paginatedUsers: (state: any, getters: any) => {
      const filtered = getters.filteredUsers;
      const start = (state.currentPage - 1) * state.pageSize;
      const end = start + state.pageSize;
      return filtered.slice(start, end);
    },

    totalPages: (state: any, getters: any) => {
      return Math.ceil(getters.filteredUsers.length / state.pageSize);
    },

    summaryStats: (state: any) => {
      const users = state.userFocusData;
      const withFocus = users.filter((u: UserFocusData) => u.activeItems.length > 0);
      const withoutFocus = users.filter((u: UserFocusData) => u.activeItems.length === 0);
      const totalItems = users.reduce((sum: number, u: UserFocusData) => sum + u.activeItems.length, 0);
      
      // Count open vs done items
      let openItems = 0;
      let doneItems = 0;
      users.forEach((u: UserFocusData) => {
        u.activeItems.forEach((item: any) => {
          if (item.status === 'done') {
            doneItems++;
          } else {
            openItems++;
          }
        });
      });
      
      return {
        totalPeople: users.length,
        withFocus: withFocus.length,
        withoutFocus: withoutFocus.length,
        totalItems,
        openItems,
        doneItems,
      };
    },

    selectedTeam: (state: any) => state.selectedTeam,
    searchKeyword: (state: any) => state.searchKeyword,
    filterNoFocus: (state: any) => state.filterNoFocus,
    filterPartnered: (state: any) => state.filterPartnered,
    filterGoalAlignment: (state: any) => state.filterGoalAlignment,
    currentPage: (state: any) => state.currentPage,
    partnerCache: (state: any) => state.partnerCache,
    allUsers: (state: any) => state.allUsers,
    allTeams: (state: any) => state.allTeams,
    allPartnerships: (state: any) => state.allPartnerships,
    // Historical focus getters
    dateRange: (state: any) => state.dateRange,
    loadingHistorical: (state: any) => state.loadingHistorical,
    historicalFocusCache: (state: any) => state.historicalFocusCache,
    historicalFocusByTeam: (state: any) => state.historicalFocusByTeam,
    loadingHistoricalTeam: (state: any) => state.loadingHistoricalTeam,
    getFocusCoverageSnapshotDaysAgo: (state: any) => (daysAgo: number, teamId: string = 'All Teams') => {
      const historicalDocs = Array.isArray(state.historicalFocusDataCache) ? state.historicalFocusDataCache : [];
      const allUsers = state.allUsers || {};
      const userIdentityByIdentifier: Record<string, string> = {};
      const activeUserIdentities = new Set<string>();
      const includeAllTeams = !teamId || teamId === 'All Teams';

      Object.keys(allUsers).forEach((userKey) => {
        const user = allUsers[userKey];
        if (!isActiveFocusUser(user)) return;
        if (!includeAllTeams && user.team !== teamId) return;

        const identity = user.email || userKey;
        activeUserIdentities.add(identity);
        collectUserIdentifierKeys(userKey, user).forEach((identifier) => {
          userIdentityByIdentifier[identifier] = identity;
        });
      });

      if (historicalDocs.length === 0 || activeUserIdentities.size === 0) {
        return null;
      }

      const cutoffDate = format(subDays(new Date(), daysAgo), 'YYYY-MM-DD 23:59:59');
      const latestSnapshotByUser: Record<string, { date: string; checklist: any }> = {};

      historicalDocs.forEach((doc: any) => {
        const docDate = normalizeFocusDateString(doc && doc.date);
        if (!docDate || docDate > cutoffDate) return;

        const rawIdentifier = String((doc && doc.email) || '').trim();
        if (!rawIdentifier) return;

        const normalizedPhone = normalizePhoneIdentifier(rawIdentifier);
        const identity =
          userIdentityByIdentifier[rawIdentifier] ||
          userIdentityByIdentifier[rawIdentifier.replace(/\./g, '||')] ||
          (normalizedPhone ? userIdentityByIdentifier[normalizedPhone] : '');

        if (!identity || !activeUserIdentities.has(identity)) return;

        const existingSnapshot = latestSnapshotByUser[identity];
        if (!existingSnapshot || docDate > existingSnapshot.date) {
          latestSnapshotByUser[identity] = {
            date: docDate,
            checklist: doc && doc.checklist,
          };
        }
      });

      const withFocus = Object.values(latestSnapshotByUser).filter((snapshot) =>
        normalizeFocusChecklist(snapshot.checklist).some((item: any) => item && item.item)
      ).length;
      const totalPeople = activeUserIdentities.size;

      return {
        withFocus,
        totalPeople,
        coverageRate: totalPeople > 0 ? (withFocus / totalPeople) * 100 : 0,
        cutoffDate,
      };
    },

    focusDistribution: (state: any) => {
      const distribution: Record<string, { name: string; count: number; people: string[] }> = {};
      
      state.userFocusData.forEach((user: UserFocusData) => {
        user.activeItems.forEach((item: FocusItem) => {
          const key = item.item.toLowerCase().trim();
          if (!distribution[key]) {
            distribution[key] = {
              name: item.item,
              count: 0,
              people: [],
            };
          }
          distribution[key].count++;
          distribution[key].people.push(user.email);
        });
      });

      return Object.values(distribution).sort((a, b) => b.count - a.count);
    },

    focusDistributionWithStatus: (state: any) => {
      const distribution: Record<string, { name: string; openCount: number; doneCount: number; total: number }> = {};
      
      state.userFocusData.forEach((user: UserFocusData) => {
        user.activeItems.forEach((item: FocusItem) => {
          const key = item.item.toLowerCase().trim();
          if (!distribution[key]) {
            distribution[key] = {
              name: item.item,
              openCount: 0,
              doneCount: 0,
              total: 0,
            };
          }
          distribution[key].total++;
          if (item.status === 'done') {
            distribution[key].doneCount++;
          } else {
            distribution[key].openCount++;
          }
        });
      });

      return Object.values(distribution).sort((a, b) => b.total - a.total);
    },

    teamMetrics: (state: any) => {
      const rawTeamData: Record<string, any> = {};

      state.userFocusData.forEach((user: UserFocusData) => {
        if (!rawTeamData[user.team]) {
          rawTeamData[user.team] = {
            teamId: user.team,
            teamName: user.team_name,
            totalPeople: 0,
            withFocus: 0,
            totalItems: 0,
            openItems: 0,
            doneItems: 0,
            totalPartnerships: 0,
          };
        }

        rawTeamData[user.team].totalPeople++;
        if (user.activeItems.length > 0) {
          rawTeamData[user.team].withFocus++;
          rawTeamData[user.team].totalItems += user.activeItems.length;

          // Count open vs done items
          user.activeItems.forEach((item: FocusItem) => {
            if (item.status === 'done') {
              rawTeamData[user.team].doneItems++;
            } else {
              rawTeamData[user.team].openItems++;
            }
          });
        }
      });

      // Calculate partnership counts per team using allPartnerships
      // Build a Set of item_ids that have at least one partner
      const itemsWithPartners = new Set<string>();
      state.allPartnerships.forEach((partnership: PartnershipData) => {
        if (partnership.item_id) {
          itemsWithPartners.add(partnership.item_id);
        }
      });

      const itemsWithPartnersByTeam: Record<string, number> = {};
      state.userFocusData.forEach((user: UserFocusData) => {
        user.activeItems.forEach((item: FocusItem) => {
          if (item.item_id && itemsWithPartners.has(item.item_id)) {
            itemsWithPartnersByTeam[user.team] = (itemsWithPartnersByTeam[user.team] || 0) + 1;
          }
        });
      });
      Object.keys(itemsWithPartnersByTeam).forEach((teamId) => {
        if (rawTeamData[teamId]) {
          rawTeamData[teamId].totalPartnerships = itemsWithPartnersByTeam[teamId];
        }
      });

      // Aggregate each team's metrics to include its child teams as well
      const organization1levelAll = state.organization1levelAll || {};
      const teamIdsToShow = Object.keys(rawTeamData); // show only teams that have users
      const aggregated: any[] = [];
      teamIdsToShow.forEach((teamId) => {
        const children = organization1levelAll[teamId] || { [teamId]: true };
        const childIds = Object.keys(children);
        const sum = {
          teamId,
          teamName: (state.allTeams[teamId] && state.allTeams[teamId].name) || rawTeamData[teamId].teamName || '',
          totalPeople: 0,
          withFocus: 0,
          totalItems: 0,
          openItems: 0,
          doneItems: 0,
          totalPartnerships: 0,
        };
        // Include the team itself explicitly if not present in map
        if (!children[teamId]) {
          childIds.push(teamId);
        }
        childIds.forEach((cid) => {
          const t = rawTeamData[cid];
          if (t) {
            sum.totalPeople += t.totalPeople || 0;
            sum.withFocus += t.withFocus || 0;
            sum.totalItems += t.totalItems || 0;
            sum.openItems += t.openItems || 0;
            sum.doneItems += t.doneItems || 0;
            sum.totalPartnerships += t.totalPartnerships || 0;
          }
        });
        aggregated.push(sum);
      });

      // Calculate percentages and averages
      return aggregated.map((team: any) => ({
        ...team,
        percentWithFocus: team.totalPeople > 0 ? (team.withFocus / team.totalPeople) * 100 : 0,
        avgItemsPerPerson: team.totalPeople > 0 ? team.totalItems / team.totalPeople : 0,
        partnershipRate: team.totalItems > 0 ? (team.totalPartnerships / team.totalItems) * 100 : 0,
        completionRate: team.totalItems > 0 ? (team.doneItems / team.totalItems) * 100 : 0,
      })).sort((a, b) => b.percentWithFocus - a.percentWithFocus);
    },

    partnersMap: (state: any) => {
      const map: Record<string, any[]> = {};

      state.allPartnerships.forEach((partnership: PartnershipData) => {
        if (!map[partnership.item_id]) {
          map[partnership.item_id] = [];
        }

        const partnerUser = state.allUsers[partnership.partner_email];
        if (partnerUser) {
          map[partnership.item_id].push({
            email: partnership.partner_email,
            fullname: `${partnerUser.firstname || ''} ${partnerUser.lastname || ''}`.trim(),
            large_pic: partnerUser.large_pic,
            house_color: partnerUser.house_color,
            team_name: (state.allTeams[partnerUser.team] && state.allTeams[partnerUser.team].name) || '',
          });
        }
      });

      // Deduplicate partners per focus item to avoid duplicate keys in v-for
      Object.keys(map).forEach((itemId) => {
        map[itemId] = _.uniqBy(map[itemId], 'email');
      });

      return map;
    },

    // Goal alignment getters (three-state: null = ignored, "unmatched" = unaligned, valid goal_id = aligned)
    alignedFocusCount: (state: any) => {
      let count = 0;
      state.userFocusData.forEach((user: UserFocusData) => {
        user.activeItems.forEach((item: FocusItem) => {
          // Aligned = has a goal_id that is not "unmatched"
          if (item.goal_id && item.goal_id !== 'unmatched') count++;
        });
      });
      return count;
    },

    unalignedFocusCount: (state: any) => {
      let count = 0;
      state.userFocusData.forEach((user: UserFocusData) => {
        user.activeItems.forEach((item: FocusItem) => {
          // Unaligned = goal_id is "unmatched" (goals exist but no match)
          if (item.goal_id === 'unmatched') count++;
        });
      });
      return count;
    },

    goalAlignmentStats: (state: any) => {
      const stats: Record<string, {
        goalId: string;
        peopleCount: number;
        openCount: number;
        doneCount: number;
        totalItems: number;
      }> = {};

      // Track unique users per goal using a plain object (Vue 2 compatible)
      const usersByGoal: Record<string, Record<string, boolean>> = {};

      state.userFocusData.forEach((user: UserFocusData) => {
        user.activeItems.forEach((item: FocusItem) => {
          // Only count items with valid goal IDs (not null, not "unmatched")
          if (item.goal_id && item.goal_id !== 'unmatched') {
            const goalId = item.goal_id;

            if (!stats[goalId]) {
              stats[goalId] = {
                goalId,
                peopleCount: 0,
                openCount: 0,
                doneCount: 0,
                totalItems: 0,
              };
              usersByGoal[goalId] = {};
            }

            stats[goalId].totalItems++;
            if (item.status === 'done') {
              stats[goalId].doneCount++;
            } else {
              stats[goalId].openCount++;
            }

            // Track unique users
            if (!usersByGoal[goalId][user.email]) {
              usersByGoal[goalId][user.email] = true;
              stats[goalId].peopleCount++;
            }
          }
        });
      });

      return stats;
    },

    // Team-scoped getters for Goals view
    focusDataByTeam: (state: any) => state.focusDataByTeam,
    loadingTeamFocus: (state: any) => state.loadingTeamFocus,

    /**
     * Get alignment stats for a specific team from the cache
     * Returns null if data not loaded, or alignment stats if available
     */
    getTeamAlignmentStats: (state: any) => (teamId: string, includeChildren: boolean = false): TeamAlignmentStats | null => {
      const cacheKey = `${teamId}_${includeChildren ? 'with_children' : 'direct'}`;
      const cached = state.focusDataByTeam[cacheKey];

      if (!cached || !cached.loaded) {
        return null;
      }
      return cached.stats || null;
    },

    /**
     * Check if focus data is loading for a specific team
     */
    isLoadingTeamFocus: (state: any) => (teamId: string, includeChildren: boolean = false): boolean => {
      const cacheKey = `${teamId}_${includeChildren ? 'with_children' : 'direct'}`;
      return !!state.loadingTeamFocus[cacheKey];
    },

    /**
     * Check if focus data is cached for a specific team
     */
    hasTeamFocusData: (state: any) => (teamId: string, includeChildren: boolean = false): boolean => {
      const cacheKey = `${teamId}_${includeChildren ? 'with_children' : 'direct'}`;
      const cached = state.focusDataByTeam[cacheKey];
      return !!(cached && cached.loaded);
    },

    /**
     * Get historical focus data for a specific team
     */
    getHistoricalFocusForTeam: (state: any) => (teamId: string, includeChildren: boolean = true): HistoricalFocusCache | null => {
      const dateRange = state.dateRange;
      const cacheKey = `${teamId || 'all'}_${dateRange}_${includeChildren ? 'with_children' : 'direct'}`;
      return state.historicalFocusByTeam[cacheKey] || null;
    },

    /**
     * Check if historical focus data is loading for a team
     */
    isLoadingHistoricalTeam: (state: any) => (teamId: string, includeChildren: boolean = true): boolean => {
      const dateRange = state.dateRange;
      const cacheKey = `${teamId || 'all'}_${dateRange}_${includeChildren ? 'with_children' : 'direct'}`;
      return !!state.loadingHistoricalTeam[cacheKey];
    },

    /**
     * Get historical summary stats for a team
     * Returns stats from historical cache if available, otherwise returns null
     */
    getHistoricalSummaryStats: (state: any) => (teamId: string, includeChildren: boolean = true) => {
      //console.time('getHistoricalSummaryStats')
      const dateRange = state.dateRange;
      const cacheKey = `${teamId || 'all'}_${dateRange}_${includeChildren ? 'with_children' : 'direct'}`;
      const cached = state.historicalFocusByTeam[cacheKey];
      if (!cached) {
        return null;
      }

      // Calculate alignment rate based on applicable items only (aligned + unaligned, excluding null)
      const applicableItems = cached.stats.alignedItems + cached.stats.unalignedItems;
      // alignmentRate is null when no applicable items (all null = N/A), otherwise calculate percentage
      const alignmentRate = applicableItems > 0 
        ? Math.round((cached.stats.alignedItems / applicableItems) * 100) 
        : null;

      return {
        dateRange: cached.dateRange,
        totalUniqueItems: cached.stats.totalUniqueItems,
        completedItems: cached.stats.completedItems,
        openItems: cached.stats.openItems,
        usersWithFocus: cached.stats.usersWithFocus,
        alignedItems: cached.stats.alignedItems,
        unalignedItems: cached.stats.unalignedItems,
        alignmentRate,
        completionRate: cached.stats.totalUniqueItems > 0 
          ? Math.round((cached.stats.completedItems / cached.stats.totalUniqueItems) * 100) 
          : 0,
      };
    },

    /**
     * Get historical goal alignment stats from cached historical data
     */
    getHistoricalGoalStats: (state: any) => (teamId: string, includeChildren: boolean = true) => {
      const dateRange = state.dateRange;
      const cacheKey = `${teamId || 'all'}_${dateRange}_${includeChildren ? 'with_children' : 'direct'}`;
      const cached = state.historicalFocusByTeam[cacheKey];
      
      if (!cached) {
        return null;
      }

      // Calculate per-goal stats from historical items
      const goalStats: Record<string, {
        goalId: string;
        peopleCount: number;
        openCount: number;
        doneCount: number;
        totalItems: number;
        people: Array<{ email: string; fullname: string; large_pic?: string; house_color?: string }>;
      }> = {};
      const usersByGoal: Record<string, Record<string, any>> = {};

      cached.allItems.forEach((item: HistoricalFocusItem) => {
        // Only count items with valid goal IDs (not null, not "unmatched")
        if (item.goal_id && item.goal_id !== 'unmatched') {
          const goalId = item.goal_id;

          if (!goalStats[goalId]) {
            goalStats[goalId] = {
              goalId,
              peopleCount: 0,
              openCount: 0,
              doneCount: 0,
              totalItems: 0,
              people: [],
            };
            usersByGoal[goalId] = {};
          }

          goalStats[goalId].totalItems++;
          if (item.status === 'done') {
            goalStats[goalId].doneCount++;
          } else {
            goalStats[goalId].openCount++;
          }

          // Track unique users with their info
          if (item.userEmail && !usersByGoal[goalId][item.userEmail]) {
            usersByGoal[goalId][item.userEmail] = true;
            goalStats[goalId].peopleCount++;
            if (item.userInfo) {
              goalStats[goalId].people.push({
                email: item.userEmail,
                fullname: item.userInfo.fullname || item.userEmail,
                large_pic: item.userInfo.large_pic,
                house_color: item.userInfo.house_color,
              });
            }
          }
        }
      });

      return goalStats;
    },

    // Weekly summary getters
    weeklySummaryByTeam: (state: any) => state.weeklySummaryByTeam,
    loadingWeeklySummary: (state: any) => state.loadingWeeklySummary,

    /**
     * Get weekly summary data for a specific team
     */
    getWeeklySummaryForTeam: (state: any) => (teamId: string): WeeklySummary[] | null => {
      const cacheKey = teamId === 'All Teams' || !teamId ? 'all' : teamId;
      return state.weeklySummaryByTeam[cacheKey] || null;
    },

    /**
     * Check if weekly summary is loading for a team
     */
    isLoadingWeeklySummary: (state: any) => (teamId: string): boolean => {
      const cacheKey = teamId === 'All Teams' || !teamId ? 'all' : teamId;
      return !!state.loadingWeeklySummary[cacheKey];
    },

    /**
     * Get week-over-week change for focus coverage
     * Returns change between current week and previous week
     */
    getFocusCoverageWeekChange: (state: any) => (teamId: string): WeekOverWeekChange => {
      const cacheKey = teamId === 'All Teams' || !teamId ? 'all' : teamId;
      const weeklySummaries = state.weeklySummaryByTeam[cacheKey];
      
      if (!weeklySummaries || weeklySummaries.length === 0) {
        return { current: 0, previous: null, change: null, changePercent: null, trend: 'no_data' };
      }

      const currentWeek = weeklySummaries[0];
      const previousWeek = weeklySummaries.length > 1 ? weeklySummaries[1] : null;
      const current = (currentWeek && currentWeek.focusCoverage) || 0;
      const previous = previousWeek ? ((previousWeek.focusCoverage) || 0) : null;
      
      if (previous === null) {
        return { current, previous: null, change: null, changePercent: null, trend: 'no_data' };
      }

      const change = current - previous;
      const changePercent = previous !== 0 ? Math.round((change / previous) * 100) : (change > 0 ? 100 : 0);
      const trend = change > 0 ? 'up' : change < 0 ? 'down' : 'neutral';

      return { current, previous, change: Math.round(change * 10) / 10, changePercent, trend };
    },

    /**
     * Get week-over-week change for goal alignment
     * Returns change between current week and previous week
     */
    getGoalAlignmentWeekChange: (state: any) => (teamId: string): WeekOverWeekChange => {
      const cacheKey = teamId === 'All Teams' || !teamId ? 'all' : teamId;
      const weeklySummaries = state.weeklySummaryByTeam[cacheKey];
      
      if (!weeklySummaries || weeklySummaries.length === 0) {
        return { current: 0, previous: null, change: null, changePercent: null, trend: 'no_data' };
      }

      const currentWeek = weeklySummaries[0];
      const previousWeek = weeklySummaries.length > 1 ? weeklySummaries[1] : null;
      const current = (currentWeek && currentWeek.alignedItemsPercentage) || 0;
      const previous = previousWeek ? ((previousWeek.alignedItemsPercentage) || 0) : null;
      
      if (previous === null) {
        return { current, previous: null, change: null, changePercent: null, trend: 'no_data' };
      }

      const change = current - previous;
      const changePercent = previous !== 0 ? Math.round((change / previous) * 100) : (change > 0 ? 100 : 0);
      const trend = change > 0 ? 'up' : change < 0 ? 'down' : 'neutral';

      return { current, previous, change: Math.round(change * 10) / 10, changePercent, trend };
    },

    /**
     * Get multi-week trend data for a metric (up to 13 weeks)
     * Used for displaying trend sparklines or detailed history
     */
    getWeeklyTrendData: (state: any) => (teamId: string, metric: 'focusCoverage' | 'alignedItemsPercentage', weeks: number = 13) => {
      const cacheKey = teamId === 'All Teams' || !teamId ? 'all' : teamId;
      const weeklySummaries = state.weeklySummaryByTeam[cacheKey];
      
      if (!weeklySummaries || weeklySummaries.length === 0) {
        return [];
      }

      return weeklySummaries
        .slice(0, weeks)
        .map((summary: WeeklySummary) => ({
          week: summary.week,
          value: summary[metric] || 0,
        }))
        .reverse(); // Oldest first for chart display
    },

    /**
     * Get goal focus trend data for line chart visualization
     * Aggregates historicalFocusDataCache by week and goal_id
     * Returns data structured for Chart.js line chart
     * @param teamId - Team ID to filter by (or 'All Teams')
     * @param includeChildren - Whether to include child teams
     * @param goalsMap - Map of goal_id to goal info (from goalsTeamAI)
     * @param topN - Limit to top N goals by total focus count (default 10)
     */
    getGoalFocusTrendData: (state: any) => (
      teamId: string,
      includeChildren: boolean = true,
      goalsMap: Record<string, { goal_id: string; quick_preview: string; team_id?: string; parent_goal_ids?: string[] }[]> = {},
      topN: number = 10
    ) => {
      const dateRange = state.dateRange;
      const cacheKey = `${teamId || 'all'}_${dateRange}_${includeChildren ? 'with_children' : 'direct'}`;
      const cached = state.historicalFocusByTeam[cacheKey];

      if (!cached || !cached.allItems || cached.allItems.length === 0) {
        return { labels: [], datasets: [] };
      }

      // Helper function to get ISO week string from date (YYYY-WW format)
      const getWeekKey = (dateStr: string): string => {
        const date = new Date(dateStr);
        const year = date.getFullYear();
        // Get the week number
        const startOfYear = new Date(year, 0, 1);
        const days = Math.floor((date.getTime() - startOfYear.getTime()) / (24 * 60 * 60 * 1000));
        const weekNum = Math.ceil((days + startOfYear.getDay() + 1) / 7);
        return `${year}-W${weekNum.toString().padStart(2, '0')}`;
      };

      // Helper function to format week label for display
      const formatWeekLabel = (weekKey: string): string => {
        const [year, week] = weekKey.split('-W');
        return `W${parseInt(week)} '${year.slice(2)}`;
      };

      // Helper function to lighten a hex color
      const lightenColor = (hex: string, percent: number): string => {
        // Remove # if present
        const cleanHex = hex.replace('#', '');
        const num = parseInt(cleanHex, 16);
        const r = Math.min(255, Math.round((num >> 16) + (255 - (num >> 16)) * percent));
        const g = Math.min(255, Math.round(((num >> 8) & 0x00FF) + (255 - ((num >> 8) & 0x00FF)) * percent));
        const b = Math.min(255, Math.round((num & 0x0000FF) + (255 - (num & 0x0000FF)) * percent));
        return '#' + (0x1000000 + (r << 16) + (g << 8) + b).toString(16).slice(1).toUpperCase();
      };

      // Build a flat lookup map from goal_id to goal info (including parent_goal_ids)
      const goalLookup: Record<string, { goal_id: string; quick_preview: string; parent_goal_ids?: string[] }> = {};
      Object.keys(goalsMap).forEach((teamKey) => {
        const goals = goalsMap[teamKey];
        if (Array.isArray(goals)) {
          goals.forEach((goal) => {
            if (goal.goal_id) {
              goalLookup[goal.goal_id] = goal;
            }
          });
        }
      });

      // Group items by week and goal_id
      const weekGoalCounts: Record<string, Record<string, number>> = {};
      const goalTotals: Record<string, number> = {};
      const allWeeks = new Set<string>();

      cached.allItems.forEach((item: HistoricalFocusItem) => {
        if (!item.date) return;

        const weekKey = getWeekKey(item.date);
        allWeeks.add(weekKey);

        if (!weekGoalCounts[weekKey]) {
          weekGoalCounts[weekKey] = {};
        }

        // Determine the goal category
        let goalKey: string;
        if (item.goal_id && item.goal_id !== 'unmatched' && goalLookup[item.goal_id]) {
          // Valid goal_id that exists in the lookup
          goalKey = item.goal_id;
        } else if (item.goal_id && item.goal_id !== 'unmatched' && !goalLookup[item.goal_id]) {
          // Orphaned goal_id (goal was deleted or doesn't exist) - treat as unmatched
          if (item.value_id && item.value_id !== 'unmatched') {
            goalKey = 'unmatched_has_value';
          } else if (item.value_id === 'unmatched') {
            goalKey = 'unmatched_no_value';
          } else {
            goalKey = 'unmatched_value_unknown';
          }
        } else if (item.goal_id === 'unmatched') {
          // Split unmatched into THREE categories based on value_id:
          // 1. Has valid value_id (not null, not undefined, not 'unmatched')
          // 2. Explicitly no value (value_id === 'unmatched')
          // 3. Value unknown/not set (value_id is null or undefined)
          if (item.value_id && item.value_id !== 'unmatched') {
            goalKey = 'unmatched_has_value';
          } else if (item.value_id === 'unmatched') {
            goalKey = 'unmatched_no_value';
          } else {
            // null/undefined value_id - value matching not done
            goalKey = 'unmatched_value_unknown';
          }
        } else {
          // null goal_id - skip these as they're not applicable
          return;
        }

        weekGoalCounts[weekKey][goalKey] = (weekGoalCounts[weekKey][goalKey] || 0) + 1;
        goalTotals[goalKey] = (goalTotals[goalKey] || 0) + 1;
      });

      // Sort weeks chronologically
      const allSortedWeeks = Array.from(allWeeks).sort();
      
      // Calculate weekly totals for percentage calculation
      const weeklyTotals: Record<string, number> = {};
      allSortedWeeks.forEach((week) => {
        let total = 0;
        Object.keys(weekGoalCounts[week] || {}).forEach((goalId) => {
          total += weekGoalCounts[week][goalId] || 0;
        });
        weeklyTotals[week] = total;
      });

      // Filter out weeks with zero focus items (no data periods like holidays)
      // This prevents the chart from "collapsing" to 0% during gaps
      const sortedWeeks = allSortedWeeks.filter((week) => weeklyTotals[week] > 0);
      const labels = sortedWeeks.map(formatWeekLabel);

      const unmatchedKeys = ['unmatched_no_value', 'unmatched_value_unknown', 'unmatched_has_value'];
      const otherGoalsKey = '__other_goals';
      const allMatchedGoalIds = Object.keys(goalTotals)
        .filter((id) => !unmatchedKeys.includes(id))
        .sort((a, b) => goalTotals[b] - goalTotals[a]);

      // Sort goals by total count and take top N (excluding unmatched types from limit).
      // Remaining goals are aggregated so each weekly stack still represents 100% of focus.
      const goalIds = allMatchedGoalIds
        .slice(0, topN);
      const hiddenGoalIds = allMatchedGoalIds.filter((id) => !goalIds.includes(id));
      const hasOtherGoals = hiddenGoalIds.length > 0;
      const otherGoalsTotal = hiddenGoalIds.reduce((sum, goalId) => sum + (goalTotals[goalId] || 0), 0);

      // Add unmatched categories to the beginning if they have data (so they render at the bottom of the stack)
      // Order: unmatched_no_value (darkest, bottom), unmatched_value_unknown (medium), unmatched_has_value (lightest, top)
      if (goalTotals['unmatched_has_value'] > 0) {
        goalIds.unshift('unmatched_has_value');
      }
      if (goalTotals['unmatched_value_unknown'] > 0) {
        goalIds.unshift('unmatched_value_unknown');
      }
      if (goalTotals['unmatched_no_value'] > 0) {
        goalIds.unshift('unmatched_no_value');
      }
      if (hasOtherGoals) {
        goalIds.push(otherGoalsKey);
      }

      // Generate refined color palette for goals
      const colors = [
        '#5E35B1', // Deep Purple
        '#3949AB', // Indigo
        '#1E88E5', // Blue
        '#039BE5', // Light Blue
        '#00ACC1', // Cyan
        '#00897B', // Teal
        '#43A047', // Green
        '#7CB342', // Light Green
        '#FDD835', // Yellow
        '#FB8C00', // Orange
      ];

      // Build color map based on parent-child relationships
      // Root goals (no parents) get base colors, child goals get lighter shades of parent's color
      const goalColorMap: Record<string, string> = {};
      const actualGoalIds = goalIds.filter((id) => !unmatchedKeys.includes(id) && id !== otherGoalsKey);

      // First pass: identify root goals and assign base colors
      let colorIndex = 0;
      actualGoalIds.forEach((goalId) => {
        const goalInfo = goalLookup[goalId];
        const parentIds = (goalInfo && goalInfo.parent_goal_ids) || [];
        // A goal is a "root" if it has no parents OR none of its parents are in our displayed goals
        const hasDisplayedParent = parentIds.some((pid: string) => actualGoalIds.includes(pid));
        if (!hasDisplayedParent) {
          goalColorMap[goalId] = colors[colorIndex % colors.length];
          colorIndex++;
        }
      });

      // Second pass: assign lighter shades to child goals based on first parent's color
      // Also track which root goal each child belongs to for grouping
      const goalRootMap: Record<string, string> = {}; // Maps goal ID to its root parent ID
      actualGoalIds.forEach((goalId) => {
        if (goalColorMap[goalId]) {
          // Root goal - maps to itself
          goalRootMap[goalId] = goalId;
          return;
        }

        const goalInfo = goalLookup[goalId];
        const parentIds = (goalInfo && goalInfo.parent_goal_ids) || [];

        // Find first parent that has a color assigned
        let parentColor: string | null = null;
        let rootParentId: string | null = null;
        for (const parentId of parentIds) {
          if (goalColorMap[parentId]) {
            parentColor = goalColorMap[parentId];
            rootParentId = goalRootMap[parentId] || parentId;
            break;
          }
        }

        if (parentColor && rootParentId) {
          // Child gets 35% lighter shade of parent's color
          goalColorMap[goalId] = lightenColor(parentColor, 0.35);
          goalRootMap[goalId] = rootParentId;
        } else {
          // Fallback: assign next base color if no parent color found
          goalColorMap[goalId] = colors[colorIndex % colors.length];
          goalRootMap[goalId] = goalId; // Acts as its own root
          colorIndex++;
        }
      });

      // Reorder goals to group parent-child families together (same color shades adjacent)
      // Sort by: root goal's focus count (desc), then root first, then children by focus count
      const rootGoalOrder = actualGoalIds
        .filter((id) => goalRootMap[id] === id) // Get root goals only
        .sort((a, b) => goalTotals[b] - goalTotals[a]); // Sort by focus count

      const reorderedGoalIds: string[] = [];
      // Add unmatched categories first (they stay at bottom of stack)
      unmatchedKeys.forEach((key) => {
        if (goalIds.includes(key)) {
          reorderedGoalIds.push(key);
        }
      });
      // Add goals grouped by root parent
      rootGoalOrder.forEach((rootId) => {
        // Add root goal first
        reorderedGoalIds.push(rootId);
        // Add children of this root, sorted by focus count
        const children = actualGoalIds
          .filter((id) => goalRootMap[id] === rootId && id !== rootId)
          .sort((a, b) => goalTotals[b] - goalTotals[a]);
        reorderedGoalIds.push(...children);
      });
      if (hasOtherGoals) {
        reorderedGoalIds.push(otherGoalsKey);
      }

      const getCountForGoalAndWeek = (goalId: string, week: string): number => {
        if (goalId === otherGoalsKey) {
          return hiddenGoalIds.reduce((sum, hiddenGoalId) => (
            sum + ((weekGoalCounts[week] && weekGoalCounts[week][hiddenGoalId]) || 0)
          ), 0);
        }
        return (weekGoalCounts[week] && weekGoalCounts[week][goalId]) || 0;
      };

      const countsByGoalId: Record<string, number[]> = {};
      const percentagesByGoalId: Record<string, number[]> = {};

      reorderedGoalIds.forEach((goalId) => {
        countsByGoalId[goalId] = sortedWeeks.map((week) => getCountForGoalAndWeek(goalId, week));
        percentagesByGoalId[goalId] = sortedWeeks.map(() => 0);
      });

      sortedWeeks.forEach((week, weekIndex) => {
        const total = weeklyTotals[week] || 0;
        if (total <= 0) return;

        let roundedTotal = 0;
        let largestGoalId = '';
        let largestCount = 0;

        reorderedGoalIds.forEach((goalId) => {
          const count = countsByGoalId[goalId][weekIndex] || 0;
          const percentage = Math.round((count / total) * 100 * 10) / 10;
          percentagesByGoalId[goalId][weekIndex] = percentage;
          roundedTotal += percentage;

          if (count > largestCount) {
            largestGoalId = goalId;
            largestCount = count;
          }
        });

        const roundingRemainder = Math.round((100 - roundedTotal) * 10) / 10;
        if (largestGoalId && roundingRemainder !== 0) {
          const adjustedPercentage = Math.round((percentagesByGoalId[largestGoalId][weekIndex] + roundingRemainder) * 10) / 10;
          percentagesByGoalId[largestGoalId][weekIndex] = Math.max(0, adjustedPercentage);
        }
      });

      // Build datasets for Chart.js with percentage values (using reordered goals)
      const datasets = reorderedGoalIds.map((goalId) => {
        const data = percentagesByGoalId[goalId] || [];
        const absoluteData = countsByGoalId[goalId] || [];

        let goalName: string;
        let color: string;
        let borderColor: string;

        if (goalId === 'unmatched_no_value') {
          // Darkest gray for items with no goal AND explicitly no value (value_id === 'unmatched')
          goalName = 'Unmatched (No Goal or Value)';
          color = '#757575'; // Material Gray 600 - darkest
          borderColor = '#616161'; // Material Gray 700
        } else if (goalId === 'unmatched_value_unknown') {
          // Medium gray for items with no goal and value not set (value_id is null/undefined)
          goalName = 'Unmatched (Value Unknown)';
          color = '#BDBDBD'; // Material Gray 400 - medium
          borderColor = '#9E9E9E'; // Material Gray 500
        } else if (goalId === 'unmatched_has_value') {
          // Lightest gray for items with no goal but HAS a value
          goalName = 'Unmatched (Has Value)';
          color = '#E0E0E0'; // Material Gray 300 - lightest
          borderColor = '#BDBDBD'; // Material Gray 400
        } else if (goalId === otherGoalsKey) {
          goalName = 'Other goals';
          color = '#94A3B8'; // Slate 400 - distinct from unmatched gray
          borderColor = '#64748B'; // Slate 500
        } else {
          const goalInfo = goalLookup[goalId];
          goalName = (goalInfo && goalInfo.quick_preview) || 'Goal ' + goalId.slice(0, 8) + '...';
          // Use parent-aware color mapping
          color = goalColorMap[goalId] || colors[0];
          borderColor = color;
        }

        const isUnmatched = unmatchedKeys.includes(goalId);

        return {
          goalId,
          goalName,
          label: goalName,
          data,
          absoluteData, // Store absolute values for tooltip
          borderColor: borderColor,
          backgroundColor: isUnmatched ? color : color + 'CC', // 80% opacity for goals, full for unmatched
          fill: true,
          tension: 0.4, // Smoother curves
          pointRadius: 0, // No points by default for clean look
          pointHoverRadius: 4, // Show points on hover
          borderWidth: 1, // Thin border to define layers
        };
      });

      // Store weekly totals for tooltip
      const weeklyTotalsArray = sortedWeeks.map((week) => weeklyTotals[week] || 0);

      // Calculate total unmatched (all types combined)
      const unmatchedNoValueTotal = goalTotals['unmatched_no_value'] || 0;
      const unmatchedValueUnknownTotal = goalTotals['unmatched_value_unknown'] || 0;
      const unmatchedHasValueTotal = goalTotals['unmatched_has_value'] || 0;
      const totalUnmatched = unmatchedNoValueTotal + unmatchedValueUnknownTotal + unmatchedHasValueTotal;

      // Calculate skipped weeks (weeks with no data - holidays, etc.)
      const skippedWeeksCount = allSortedWeeks.length - sortedWeeks.length;

      return {
        labels,
        datasets,
        weeklyTotals: weeklyTotalsArray, // For tooltip display
        // Include goal color map for table dot colors to match chart
        goalColorMap,
        // Include summary stats for display
        summary: {
          totalWeeks: sortedWeeks.length,
          totalGoals: allMatchedGoalIds.length,
          displayedGoals: actualGoalIds.length,
          totalGoalCount: allMatchedGoalIds.length,
          hiddenGoals: hiddenGoalIds.length,
          hasOtherGoals,
          otherGoalsTotal,
          hasUnmatched: totalUnmatched > 0,
          unmatchedTotal: totalUnmatched,
          unmatchedNoValueTotal, // Items with no goal AND explicitly no value (value_id === 'unmatched')
          unmatchedValueUnknownTotal, // Items with no goal and value not set (value_id is null/undefined)
          unmatchedHasValueTotal, // Items with no goal but HAS a value
          skippedWeeks: skippedWeeksCount, // Weeks with no focus data (holidays, etc.)
        },
      };
    },

    /**
     * Get total focus items count from a specific cache
     * Used to calculate percentage of focus for emergent goals
     * @param customCacheKey - Cache key to get total from
     */
    getTotalFocusItemsCount: (state: any) => (customCacheKey: string): number => {
      const cached = state.historicalFocusByTeam[customCacheKey];
      if (!cached || !cached.allItems) {
        return 0;
      }
      return cached.allItems.length;
    },

    /**
     * Get unmatched focus items grouped by focus item text
     * Returns items where goal_id === 'unmatched', grouped to show patterns
     * Each group contains the focus item text, count, list of people working on it,
     * and value association information
     * @param teamId - Team ID to filter by (or 'All Teams')
     * @param includeChildren - Whether to include child teams
     * @param customCacheKey - Optional custom cache key (e.g., for specific date range)
     */
    getUnmatchedFocusItems: (state: any) => (
      teamId: string,
      includeChildren: boolean = true,
      customCacheKey?: string
    ): Array<{
      itemText: string;
      description: string;
      count: number;
      item_ids: string[]; // All unique item_ids for this grouped item
      hasValue: boolean; // Whether this item has a value association
      valueStatus: 'has_value' | 'no_value' | 'unknown'; // Detailed value status
      value_id: string | null | undefined; // The value_id if present
      people: Array<{
        email: string;
        fullname: string;
        large_pic?: string;
        house_color?: string;
        team_name?: string;
        team_id?: string; // Team ID for team attribution
      }>;
    }> => {
      // Use custom cache key if provided, otherwise use default based on state.dateRange
      const cacheKey = customCacheKey || `${teamId || 'all'}_${state.dateRange}_${includeChildren ? 'with_children' : 'direct'}`;
      const cached = state.historicalFocusByTeam[cacheKey];

      if (!cached || !cached.allItems || cached.allItems.length === 0) {
        return [];
      }

      // Filter for unmatched items only
      const unmatchedItems = cached.allItems.filter(
        (item: HistoricalFocusItem) => item.goal_id === 'unmatched'
      );

      if (unmatchedItems.length === 0) {
        return [];
      }

      // Group by normalized item text (lowercase, trimmed)
      const groupedByText: Record<string, {
        itemText: string;
        description: string;
        value_id: string | null | undefined; // Track value_id from first item in group
        item_ids: Set<string>; // Track all unique item_ids
        peopleMap: Record<string, {
          email: string;
          fullname: string;
          large_pic?: string;
          house_color?: string;
          team_name?: string;
          team_id?: string; // Team ID for team attribution
        }>;
      }> = {};

      unmatchedItems.forEach((item: HistoricalFocusItem) => {
        const normalizedText = (item.item || '').toLowerCase().trim();
        if (!normalizedText) return;

        if (!groupedByText[normalizedText]) {
          groupedByText[normalizedText] = {
            itemText: item.item, // Keep original case for display
            description: item.description || '', // Include description
            value_id: item.value_id, // Track value_id from first occurrence
            item_ids: new Set<string>(),
            peopleMap: {},
          };
        }

        // Track item_id for this focus item
        if (item.item_id) {
          groupedByText[normalizedText].item_ids.add(item.item_id);
        }

        // Add person if not already added (dedupe by email)
        if (item.userEmail && !groupedByText[normalizedText].peopleMap[item.userEmail]) {
          groupedByText[normalizedText].peopleMap[item.userEmail] = {
            email: item.userEmail,
            fullname: (item.userInfo && item.userInfo.fullname) || item.userEmail,
            large_pic: item.userInfo && item.userInfo.large_pic,
            house_color: item.userInfo && item.userInfo.house_color,
            team_name: item.userInfo && item.userInfo.team_name,
            team_id: item.userInfo && item.userInfo.team, // Team ID for team attribution
          };
        }
      });

      // Convert to array and sort by people count (most people first)
      const result = Object.values(groupedByText).map((group) => {
        // Determine valueStatus based on value_id:
        // - 'has_value': valid value_id (not null, not undefined, not 'unmatched')
        // - 'no_value': value_id === 'unmatched' (explicitly no value)
        // - 'unknown': value_id is null or undefined (value not set)
        let valueStatus: 'has_value' | 'no_value' | 'unknown';
        if (group.value_id && group.value_id !== 'unmatched') {
          valueStatus = 'has_value';
        } else if (group.value_id === 'unmatched') {
          valueStatus = 'no_value';
        } else {
          valueStatus = 'unknown';
        }
        
        const hasValue = valueStatus === 'has_value';
        
        return {
          itemText: group.itemText,
          description: group.description,
          count: Object.keys(group.peopleMap).length,
          item_ids: Array.from(group.item_ids),
          hasValue,
          valueStatus,
          value_id: group.value_id,
          people: Object.values(group.peopleMap),
        };
      });

      // Sort by count descending, then alphabetically by item text
      result.sort((a, b) => {
        if (b.count !== a.count) return b.count - a.count;
        return a.itemText.localeCompare(b.itemText);
      });

      return result;
    },

    /**
     * Get unassigned focus items for Discover Goals (teams with no goals yet)
     * Returns items where goal_id === null, grouped to show patterns.
     *
     * IMPORTANT: This is intended for Emergent Goals discovery ONLY when there are
     * zero in-scope goals. Elsewhere, goal_id === null continues to mean "ignored".
     *
     * Shape matches getUnmatchedFocusItems so the UI can reuse formatting.
     */
    getUnassignedFocusItemsForDiscovery: (state: any) => (
      teamId: string,
      includeChildren: boolean = true,
      customCacheKey?: string
    ): Array<{
      itemText: string;
      description: string;
      count: number;
      item_ids: string[];
      hasValue: boolean;
      valueStatus: 'has_value' | 'no_value' | 'unknown';
      value_id: string | null | undefined;
      people: Array<{
        email: string;
        fullname: string;
        large_pic?: string;
        house_color?: string;
        team_name?: string;
        team_id?: string;
      }>;
    }> => {
      const cacheKey = customCacheKey || `${teamId || 'all'}_${state.dateRange}_${includeChildren ? 'with_children' : 'direct'}`;
      const cached = state.historicalFocusByTeam[cacheKey];

      if (!cached || !cached.allItems || cached.allItems.length === 0) {
        return [];
      }

      // Filter for unassigned items only (goal_id === null)
      const unassignedItems = cached.allItems.filter(
        (item: HistoricalFocusItem) => item.goal_id === null
      );

      if (unassignedItems.length === 0) {
        return [];
      }

      // Group by normalized item text (lowercase, trimmed)
      const groupedByText: Record<string, {
        itemText: string;
        description: string;
        value_id: string | null | undefined;
        item_ids: Set<string>;
        peopleMap: Record<string, {
          email: string;
          fullname: string;
          large_pic?: string;
          house_color?: string;
          team_name?: string;
          team_id?: string;
        }>;
      }> = {};

      unassignedItems.forEach((item: HistoricalFocusItem) => {
        const normalizedText = (item.item || '').toLowerCase().trim();
        if (!normalizedText) return;

        if (!groupedByText[normalizedText]) {
          groupedByText[normalizedText] = {
            itemText: item.item,
            description: item.description || '',
            value_id: item.value_id,
            item_ids: new Set<string>(),
            peopleMap: {},
          };
        }

        if (item.item_id) {
          groupedByText[normalizedText].item_ids.add(item.item_id);
        }

        if (item.userEmail && !groupedByText[normalizedText].peopleMap[item.userEmail]) {
          groupedByText[normalizedText].peopleMap[item.userEmail] = {
            email: item.userEmail,
            fullname: (item.userInfo && item.userInfo.fullname) || item.userEmail,
            large_pic: item.userInfo && item.userInfo.large_pic,
            house_color: item.userInfo && item.userInfo.house_color,
            team_name: item.userInfo && item.userInfo.team_name,
            team_id: item.userInfo && item.userInfo.team,
          };
        }
      });

      const result = Object.values(groupedByText).map((group) => {
        let valueStatus: 'has_value' | 'no_value' | 'unknown';
        if (group.value_id && group.value_id !== 'unmatched') {
          valueStatus = 'has_value';
        } else if (group.value_id === 'unmatched') {
          valueStatus = 'no_value';
        } else {
          valueStatus = 'unknown';
        }

        const hasValue = valueStatus === 'has_value';

        return {
          itemText: group.itemText,
          description: group.description,
          count: Object.keys(group.peopleMap).length,
          item_ids: Array.from(group.item_ids),
          hasValue,
          valueStatus,
          value_id: group.value_id,
          people: Object.values(group.peopleMap),
        };
      });

      result.sort((a, b) => {
        if (b.count !== a.count) return b.count - a.count;
        return a.itemText.localeCompare(b.itemText);
      });

      return result;
    },

      /**
     * Get historical summary stats for a team
     * Returns stats from historical cache if available, otherwise returns null
     */
      getQuarterlySummaryStats: (state: any) => (teamId: string, includeChildren: boolean = true) => {
        //console.time('getHistoricalSummaryStats')
        const dateRange = state.dateRange;
        
        const cached = state.quarterlyFocusByTeam;

        if (!cached) {
          return null;
        }
  
        // Calculate alignment rate based on applicable items only (aligned + unaligned, excluding null)
        const applicableItems = cached.stats.alignedItems + cached.stats.unalignedItems;
        // alignmentRate is null when no applicable items (all null = N/A), otherwise calculate percentage
        const alignmentRate = applicableItems > 0 
          ? Math.round((cached.stats.alignedItems / applicableItems) * 100) 
          : null;
        return {
          dateRange: cached.dateRange,
          totalUniqueItems: cached.stats.totalUniqueItems,
          completedItems: cached.stats.completedItems,
          openItems: cached.stats.openItems,
          usersWithFocus: cached.stats.usersWithFocus,
          alignedItems: cached.stats.alignedItems,
          unalignedItems: cached.stats.unalignedItems,
          alignmentRate,
          completionRate: cached.stats.totalUniqueItems > 0 
            ? Math.round((cached.stats.completedItems / cached.stats.totalUniqueItems) * 100) 
            : 0,
        };
      },
  
      /**
       * Get historical goal alignment stats from cached historical data
       */
      getQuarterlyGoalStats: (state: any) => (teamId: string, includeChildren: boolean = true) => {
        const dateRange = state.dateRange;
        const cached = state.quarterlyFocusByTeam;
        
        if (!cached) {
          return null;
        }
  
        // Calculate per-goal stats from historical items
        const goalStats: Record<string, {
          goalId: string;
          peopleCount: number;
          openCount: number;
          doneCount: number;
          totalItems: number;
          people: Array<{ email: string; fullname: string; large_pic?: string; house_color?: string }>;
        }> = {};
        const usersByGoal: Record<string, Record<string, any>> = {};
  
        cached.allItems.forEach((item: HistoricalFocusItem) => {
          // Only count items with valid goal IDs (not null, not "unmatched")
          if (item.goal_id && item.goal_id !== 'unmatched') {
            const goalId = item.goal_id;
  
            if (!goalStats[goalId]) {
              goalStats[goalId] = {
                goalId,
                peopleCount: 0,
                openCount: 0,
                doneCount: 0,
                totalItems: 0,
                people: [],
              };
              usersByGoal[goalId] = {};
            }
  
            goalStats[goalId].totalItems++;
            if (item.status === 'done') {
              goalStats[goalId].doneCount++;
            } else {
              goalStats[goalId].openCount++;
            }
  
            // Track unique users with their info
            if (item.userEmail && !usersByGoal[goalId][item.userEmail]) {
              usersByGoal[goalId][item.userEmail] = true;
              goalStats[goalId].peopleCount++;
              if (item.userInfo) {
                goalStats[goalId].people.push({
                  email: item.userEmail,
                  fullname: item.userInfo.fullname || item.userEmail,
                  large_pic: item.userInfo.large_pic,
                  house_color: item.userInfo.house_color,
                });
              }
            }
          }
        });
        return goalStats;
      },
  },
};
